import React, { useCallback, useState, useEffect, useRef, useId } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardNavBar from "../DashboardNavBar";
import Footer from "../../../app/landing/Footer";
import StyleSelection from "./StyleSelection";
import TemplateSelection from "./TemplateSelection";
import Website from "../../../renderer/pages/Website";
import PreviewDeviceStage from "../../../renderer/preview/PreviewDeviceStage";
import { usePreview } from "../../context/PreviewContext";
import {
  MobileOutlined,
  DesktopOutlined,
  CloudUploadOutlined,
  EditOutlined,
  CloseOutlined,
  FileOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ArrowLeftIcon } from "../../../design-system";
import { apiFetch } from "../../../core/utils/api";
import { APP_DOMAIN } from "../../../core/utils/config";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import { NetworkErrorState } from "../../../design-system/components/layouts";
import SubscriptionPlansModal from "../../../subscription/SubscriptionPlansModal";
import { extractRouteParams } from "../../../core/utils/urlUtils";
import { normalizeSubdomain } from "../../../core/utils/subdomainUtils";
import { templatePreviewUrl } from "../../../core/utils/previewUrls";
import {
  uploadAIInputAsset,
  updateAIInputAsset,
  deleteAIInputAsset,
  detachAIInputAssetFromWebsite,
  getAIInputAsset,
  retryAIInputAssetExtraction,
  listAIInputAssets,
} from "../../../core/utils/aiInputAssetsApi";
import useBreakpoint from "../../../core/hooks/useBreakpoint";
import LoadingPage from "../../../renderer/utils/LoadingPage";

const PREVIEW_DEBOUNCE_MS = process.env.NODE_ENV === "test" ? 0 : 350;
const GENERATION_POLL_INTERVAL_MS = process.env.NODE_ENV === "test" ? 0 : 2000;
// Backend generation can sometimes take a few minutes; keep polling long enough that we don't give up early.
// Planning (gpt-5.5) ≈ 2–5 min, parallel images ≈ 1–2 min, content apply ≈ 1 min → 15 min headroom.
const GENERATION_POLL_TIMEOUT_MS = 15 * 60 * 1000;
const GENERATION_MAX_CONSECUTIVE_POLL_ERRORS = 6;
const GENERATION_POLL_ERROR_BACKOFF_MAX_MS = 15_000;
// One silent auto-retry for transient AI generation start failures / early job failures.
const GENERATION_START_AUTO_RETRY_DELAY_MS = process.env.NODE_ENV === "test" ? 0 : 900;
// If the backend job status becomes "failed" within this window after start, attempt one silent restart.
const GENERATION_EARLY_FAILURE_WINDOW_MS = 30_000;
// NOTE: generation polling failures are handled via backoff; a failed job is shown to the user with retry actions.
const GENERATION_UI_PHASES = [
  { label: "Reading", ms: 3_000 },
  { label: "Analysing", ms: 10_000 },
  { label: "Structuring", ms: 10_000 },
  { label: "Building", ms: 18_000 },
  { label: "Generating", ms: 18_000 },
  { label: "Refining", ms: 8_000 },
  { label: "Polishing", ms: 6_000 },
  { label: "Testing", ms: 6_000 },
];
const GENERATION_UI_DOT_STEP_MS = 650; // not too fast
const GENERATION_UI_DOTS = [".", "..", "..."];

// Automatic retries: keep them conservative (only once) to avoid loops/spam.
const UPLOAD_AUTO_RETRY_DELAY_MS = process.env.NODE_ENV === "test" ? 0 : 900;
const UPLOAD_AUTO_RETRY_MAX_RETRIES = 1; // retry once after the first failure
const EXTRACTION_AUTO_RETRY_DELAY_MS = process.env.NODE_ENV === "test" ? 0 : 1200;
const UPLOAD_TIMEOUT_MS = process.env.NODE_ENV === "test" ? 5000 : 5 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const playGenerationSuccessChime = () => {
  if (typeof window === "undefined") return;
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) return;

  let audioContext;
  try {
    audioContext = new AudioContextCtor();
  } catch (e) {
    return;
  }

  const scheduleNote = (frequencyHz, startOffsetSeconds, durationSeconds, peakGain = 0.11) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const startTime = audioContext.currentTime + startOffsetSeconds;
    const endTime = startTime + durationSeconds;

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequencyHz, startTime);
    gainNode.gain.setValueAtTime(0.0001, startTime);
    gainNode.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.03);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, endTime);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start(startTime);
    oscillator.stop(endTime + 0.02);
  };

  const play = () => {
    scheduleNote(880, 0, 0.16, 0.1);
    scheduleNote(1174.66, 0.18, 0.2, 0.12);
    window.setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 900);
  };

  if (audioContext.state === "suspended") {
    audioContext
      .resume()
      .then(play)
      .catch(() => {
        audioContext.close().catch(() => {});
      });
  } else {
    play();
  }
};

const IMAGE_FILENAME_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "tif",
  "tiff",
  "ico",
  "heic",
  "heif",
  "avif",
]);

const getFilenameExtension = (filename) => {
  const value = String(filename || "").trim();
  if (!value) return "";
  const dotIndex = value.lastIndexOf(".");
  if (dotIndex <= 0 || dotIndex === value.length - 1) return "";
  return value.slice(dotIndex + 1).toLowerCase();
};

const isImageFilename = (filename) => IMAGE_FILENAME_EXTENSIONS.has(getFilenameExtension(filename));

const LandscapeImageIcon = ({ className = "w-5 h-5" }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="8" cy="10" r="1.5" fill="currentColor" />
    <path
      d="M5.5 17l4-4a1 1 0 0 1 1.4 0l2 2 2.6-2.6a1 1 0 0 1 1.4 0L19 14.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DiagonalExpandIcon = ({ className = "w-4 h-4", inward = false }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {inward ? (
      <>
        <path d="M6 6L11 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 11H11V8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18 18L13 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M16 13H13V16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ) : (
      <>
        <path d="M11 11L6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M9 6H6V9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13 13L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M15 18H18V15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
  </svg>
);

const getHttpStatusFromErrorMessage = (message) => {
  const match = String(message || "").match(/\((\d{3})\)/);
  if (!match) return null;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : null;
};

const shouldAutoRetryUploadError = (error) => {
  const msg = String(error?.message || "");
  const lower = msg.toLowerCase();

  // Don't retry explicit client-side problems.
  if (lower.includes("file is required")) return false;
  if (lower.includes("missing file")) return false;
  if (lower.includes("file too large")) return false;
  if (lower.includes("aborted")) return false;

  // If the backend responded with a status, only retry on retryable ones.
  const status = getHttpStatusFromErrorMessage(msg);
  if (status != null) {
    if (status >= 500) return true;
    if (status === 408 || status === 429) return true;
    return false;
  }

  // Network errors / invalid responses: retry once.
  return true;
};

const shouldAutoRetryGenerationError = (error) => {
  const msg = String(error?.message || "");
  const lower = msg.toLowerCase();

  // Don't retry explicit client-side problems / validation-like errors.
  if (lower.includes("authentication required")) return false;
  if (lower.includes("website not found")) return false;
  if (lower.includes("ai_input_asset_ids")) return false;
  if (lower.includes("selected assets")) return false;
  if (lower.includes("must belong to this website")) return false;
  if (lower.includes("not completed")) return false;

  // Hard AI quota/billing exhaustion (OpenAI 429 "insufficient_quota") can never
  // succeed on retry — retrying just spawns more failing jobs. Treat as non-retryable.
  // Transient rate-limit 429s (no quota/billing wording) are still retried below.
  if (lower.includes("exceeded your current quota")) return false;
  if (lower.includes("insufficient_quota")) return false;
  if (lower.includes("check your plan and billing")) return false;

  const status = getHttpStatusFromErrorMessage(msg);
  if (status != null) {
    if (status >= 500) return true;
    if (status === 408 || status === 429) return true;
    return false;
  }

  // Network/unknown: retry once.
  return true;
};
const PREFERRED_STYLE_ORDER = [
  "clean",
  "minimal",
  "elegant",
  "diffused",
  "bloom",
  "coastal",
  "arctic",
  "golden",
  "autumn",
  "midnight",
];
const PREFERRED_TEMPLATE_ORDER = ["navbar-navigation", "sidebar-navigation", "single-page"];
const DEFAULT_TEMPLATE_VALUE = "navbar-navigation";
const PREFERRED_STYLE_ORDER_MAP = new Map(
  PREFERRED_STYLE_ORDER.map((name, index) => [name, index])
);
const PREFERRED_TEMPLATE_ORDER_MAP = new Map(
  PREFERRED_TEMPLATE_ORDER.map((name, index) => [name, index])
);

const normalizeStyleTemplateOrderKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getStyleTemplateLabelForOrdering = (entry) =>
  normalizeStyleTemplateOrderKey(
    entry?.value ||
      entry?.slug ||
      entry?.name ||
      entry?.label ||
      entry?.display_name ||
      entry?.title ||
      ""
  );

const sortByPreferredOrder = (items, preferredOrderMap) => {
  if (!Array.isArray(items)) return [];

  return [...items].sort((a, b) => {
    const aLabel = getStyleTemplateLabelForOrdering(a);
    const bLabel = getStyleTemplateLabelForOrdering(b);
    const aOrder = preferredOrderMap.has(aLabel)
      ? preferredOrderMap.get(aLabel)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = preferredOrderMap.has(bLabel)
      ? preferredOrderMap.get(bLabel)
      : Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) return aOrder - bOrder;
    return aLabel.localeCompare(bLabel);
  });
};

const replaceCreateFlowTemplateNavigationTerms = (value) =>
  String(value || "")
    .replace(/\bNavbar\b/g, "Top")
    .replace(/\bnavbar\b/g, "top")
    .replace(/\bSidebar\b/g, "Side")
    .replace(/\bsidebar\b/g, "side");

const normalizeCreateFlowTemplateDescription = (value) =>
  replaceCreateFlowTemplateNavigationTerms(
    String(value || "")
      .replace(/page links in the navbar/gi, "page links in the top navigation")
      .replace(/sticky sidebar layout/gi, "sticky side navigation layout")
  );

const normalizeCreateFlowTemplateOption = (template) => {
  if (!template || typeof template !== "object") return template;

  return {
    ...template,
    ...(template.name != null
      ? { name: replaceCreateFlowTemplateNavigationTerms(template.name) }
      : {}),
    ...(template.label != null
      ? { label: replaceCreateFlowTemplateNavigationTerms(template.label) }
      : {}),
    ...(template.display_name != null
      ? { display_name: replaceCreateFlowTemplateNavigationTerms(template.display_name) }
      : {}),
    ...(template.title != null
      ? { title: replaceCreateFlowTemplateNavigationTerms(template.title) }
      : {}),
    ...(template.description != null
      ? { description: normalizeCreateFlowTemplateDescription(template.description) }
      : {}),
  };
};

const OUTLINE_HELP_FORMAT_DEFAULT = 3; // 1=all_bullets, 2=more_bullets, 3=mixed, 4=more_text, 5=all_text
const OUTLINE_HELP_TONE_OPTIONS = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "casual", label: "Casual" },
  { value: "bold", label: "Bold" },
];
const OUTLINE_HELP_NARRATE_AS_THIRD_PERSON_DEFAULT = false;
const OUTLINE_HELP_NARRATION_OPTIONS = [
  { thirdPerson: false, label: "1st person" },
  { thirdPerson: true, label: "3rd person" },
];
const OUTLINE_HELP_PRIMARY_GOAL_OPTIONS = [
  { value: "job_search", label: "Job search" },
  { value: "graduate_school", label: "Graduate school" },
  { value: "research_academic", label: "Research/academic" },
  { value: "freelance_clients", label: "Freelance/clients" },
  { value: "personal_brand", label: "Personal brand" },
  { value: "other", label: "Other" },
];
const OUTLINE_HELP_SECTION_OPTIONS = [
  { value: "short_bio", label: "Short bio" },
  { value: "key_achievements", label: "Key achievements" },
  { value: "current_role", label: "Current role" },
  { value: "academic_history", label: "Academic history" },
  { value: "work_history", label: "Work history" },
  { value: "skills", label: "Skills" },
  { value: "projects", label: "Projects" },
  { value: "publications", label: "Publications" },
  { value: "contact", label: "Contact info" },
  { value: "goals", label: "Goals" },
];
const OUTLINE_HELP_TEXT_LENGTH_DEFAULT = 3; // middle = unanswered

const CV_QUESTIONNAIRE_QUESTIONS = [
  { key: "current_and_goals", label: "What do you currently do, and what are your professional goals?" },
  { key: "work_experience", label: "Describe your work experience", hint: "Include roles, companies, and dates if possible" },
  { key: "education", label: "Describe your education", hint: "Include degrees, institutions, and dates if possible" },
  { key: "projects_and_research", label: "Any notable projects, research, publications, or volunteer work?" },
];

const PreviewWidthSync = ({ width, children }) => {
  const { setContainerWidth } = usePreview();

  useEffect(() => {
    if (typeof width === "number" && !Number.isNaN(width)) {
      setContainerWidth(width);
    }
  }, [width, setContainerWidth]);

  return children;
};

const PreviewWebsiteRouter = ({ username, domain, subdomain, versionSlug, onReady }) => {
  if (!username || !domain) return null;
  return (
    <Website
      username={username}
      domain={domain}
      subdomain={subdomain}
      version_slug={versionSlug}
      onReady={onReady}
    />
  );
};

const CreateNewWebsite = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" && Number.isFinite(window.innerWidth)
      ? window.innerWidth
      : 1200
  );
  const highlightTimerRef = useRef(null);
  const [highlightedStepKey, setHighlightedStepKey] = useState(null); // details|assets|prompt|style_template|null

  useEffect(() => {
    const handleResize = () => {
      const w = window?.innerWidth;
      if (typeof w === "number" && Number.isFinite(w)) {
        setWindowWidth(w);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Responsive items-per-page for style/template selectors:
  // mobile (extra_small/small) → 2, iPad/tablet (medium) → 3, desktop+ → 5
  const styleItemsPerPage =
    breakpoint === "extra_small" || breakpoint === "small"
      ? 2
      : breakpoint === "medium"
      ? 3
      : 5;

  const sparklesGradientIdRaw = useId();
  const sparklesGradientId = `ai-sparkles-gradient-${String(sparklesGradientIdRaw).replace(/:/g, "")}`;
  
  const [websiteName, setWebsiteName] = useState("");
  const [domain, setDomain] = useState("");
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableStyles, setAvailableStyles] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [stylePagination, setStylePagination] = useState({ currentPage: 0, totalPages: 0, isExpanded: false });
  const [isLoadingStyleTemplateOptions, setIsLoadingStyleTemplateOptions] = useState(true);
  const [styleTemplateOptionsError, setStyleTemplateOptionsError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationJobId, setGenerationJobId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null); // queued|running|completed|failed|null
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [isGenerationErrorModalOpen, setIsGenerationErrorModalOpen] = useState(false);
  const [generationPhaseLabel, setGenerationPhaseLabel] = useState("Reading");
  // Used to force a periodic re-render while generating so the dots keep animating
  // even when the phase label doesn't change (React bails out on setting identical strings).
  const [, setGenerationUiTick] = useState(0);
  const [shouldAutoUpdateDomain, setShouldAutoUpdateDomain] = useState(true);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [previewPages, setPreviewPages] = useState([]);
  const [previewSubdomain, setPreviewSubdomain] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewPagesLoading, setIsPreviewPagesLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0);
  const [previewMode, setPreviewMode] = useState("responsive"); // responsive|phone|desktop
  const [previewScreenshotFailed, setPreviewScreenshotFailed] = useState(false);
  const [previewWebsiteReady, setPreviewWebsiteReady] = useState(false);
  // The screenshot is shown by default; the live website replaces it once the user
  // hovers (or taps) the preview, and then stays live until a different template/style
  // is selected (a new preview resets this). The live website renders behind the
  // screenshot from the moment a template is selected, so the swap stays smooth.
  const [isLivePreviewRevealed, setIsLivePreviewRevealed] = useState(false);
  const [hoverHintArea, setHoverHintArea] = useState(null); // null|details|assets|prompt|preview
  const [aiPrompt, setAiPrompt] = useState("");
  const [isAiPromptFocused, setIsAiPromptFocused] = useState(false);
  const [outlineHelpPrimaryGoal, setOutlineHelpPrimaryGoal] = useState(null); // job_search|graduate_school|research_academic|freelance_clients|personal_brand|other|null
  const [outlineHelpPrimaryGoalOtherText, setOutlineHelpPrimaryGoalOtherText] = useState("");
  const [outlineHelpTone, setOutlineHelpTone] = useState(null); // formal|professional|friendly|bold|null
  const [outlineHelpNarrateAsThirdPerson, setOutlineHelpNarrateAsThirdPerson] = useState(
    OUTLINE_HELP_NARRATE_AS_THIRD_PERSON_DEFAULT
  ); // false = first person (default), true = third person
  const [outlineHelpFormat, setOutlineHelpFormat] = useState(OUTLINE_HELP_FORMAT_DEFAULT); // 1..5 (3 = unanswered)
  const [outlineHelpSections, setOutlineHelpSections] = useState([]); // string[] of OUTLINE_HELP_SECTION_OPTIONS values
  const [outlineHelpTextLength, setOutlineHelpTextLength] = useState(OUTLINE_HELP_TEXT_LENGTH_DEFAULT); // 1..5 (3 = unanswered)
  const styleTemplateCardRef = useRef(null);
  const detailsCardRef = useRef(null);
  const assetsCardRef = useRef(null);
  const promptCardRef = useRef(null);
  const streamlineBarRef = useRef(null);
  const [inputAssets, setInputAssets] = useState([]); // [{localId, file, assetType, comment, uploadStatus, progress, serverId, uploadError, extractionStatus}]
  const [useProfilePictureForProfessionalImages, setUseProfilePictureForProfessionalImages] = useState(true);
  const [createdWebsiteInfo, setCreatedWebsiteInfo] = useState(null); // {website_id, container_id, domain, version_slug}
  const [postCreateMessage, setPostCreateMessage] = useState("");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [createLimitInfo, setCreateLimitInfo] = useState(null); // { plan_display_name, max_websites, current_websites }
  const [existingAssets, setExistingAssets] = useState([]); // library assets from backend
  const [isLoadingExistingAssets, setIsLoadingExistingAssets] = useState(false);
  const [existingAssetsError, setExistingAssetsError] = useState("");
  const [isLibraryPickerExpanded, setIsLibraryPickerExpanded] = useState(false);
  const [librarySearchInput, setLibrarySearchInput] = useState("");
  const [librarySearchQuery, setLibrarySearchQuery] = useState("");
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const filePickerRef = useRef(null);
  const extractionPollersRef = useRef({}); // { [serverId]: timeoutId }
  const unmountedRef = useRef(false);
  const uploadInFlightRef = useRef(new Set()); // localId set
  const extractionRetryInFlightRef = useRef(new Set()); // serverId set
  const autoExtractionRetryDoneRef = useRef(new Set()); // serverId set (auto retry only once per asset)
  const previewEnsureTimerRef = useRef(null);
  const previewRequestIdRef = useRef(0);
  const generationPollerRef = useRef(null);
  const generationStartedAtRef = useRef(null);
  const generationConsecutivePollErrorsRef = useRef(0);
  const generationStartParamsRef = useRef(null); // { websiteId, aiInputAssetIds, aiPrompt, templateName }
  const generationAutoRetryUsedRef = useRef({ start: false, earlyFailure: false });
  const generationSuccessSoundPlayedRef = useRef(false);
  
  // Validation state
  const [existingNames, setExistingNames] = useState([]);
  const [existingDomains, setExistingDomains] = useState([]);
  const [nameValidation, setNameValidation] = useState({ isValid: true, message: "" });
  const [domainValidation, setDomainValidation] = useState({ isValid: true, message: "" });

  // Only keep "Suggestion 4" (streamline).
  const isStreamline = true;
  const canCreate = websiteName.trim() && domain.trim() && selectedStyle && selectedTemplate && nameValidation.isValid && domainValidation.isValid;
  const hasActiveUploads = inputAssets.some((a) => a.uploadStatus === "uploading" || a.uploadStatus === "queued");
  const hasUploadedImage = inputAssets.some((a) => a.assetType === "image" && a.uploadStatus === "uploaded" && a.serverId);
  const selectedLibraryServerIds = inputAssets
    .filter((a) => a?.source === "library" && a?.serverId)
    .map((a) => a.serverId);
  const availableCompletedExistingAssets = (existingAssets || [])
    .filter((a) => a?.status === "completed")
    .filter((a) => !selectedLibraryServerIds.includes(a.id));
  const showLibraryPicker = !!existingAssetsError || availableCompletedExistingAssets.length > 0;
  const useExpandedLibraryPickerLayout = showLibraryPicker && isLibraryPickerExpanded;
  const libraryPickerMaxHeightClass = useExpandedLibraryPickerLayout
    ? "max-h-[320px] sm:max-h-[420px]"
    : "max-h-[128px]";
  const isPreviewBusy = isPreviewLoading || isPreviewPagesLoading;
  const previewReady = Boolean(previewInfo?.username && previewInfo?.domain);
  const previewScreenshotSrc = previewInfo?.domain
    ? templatePreviewUrl(previewInfo.domain)
    : null;
  const useScreenshotPreview =
    previewMode === "responsive" && previewScreenshotSrc && !previewScreenshotFailed;
  const detailsComplete = Boolean(
    websiteName.trim() &&
      domain.trim() &&
      nameValidation.isValid &&
      domainValidation.isValid
  );
  const styleComplete = Boolean(selectedStyle);
  const templateComplete = Boolean(selectedTemplate);
  const normalizedAiPrompt = String(aiPrompt || "").trim();
  const hasStructuredPromptSelections = Boolean(
    (outlineHelpPrimaryGoal === "other" && outlineHelpPrimaryGoalOtherText.trim()) ||
      (outlineHelpPrimaryGoal && outlineHelpPrimaryGoal !== "other") ||
      outlineHelpTone ||
      (Array.isArray(outlineHelpSections) && outlineHelpSections.length > 0) ||
      outlineHelpFormat !== OUTLINE_HELP_FORMAT_DEFAULT ||
      outlineHelpTextLength !== OUTLINE_HELP_TEXT_LENGTH_DEFAULT
  );
  const questionnaireHasAnswers = Object.values(questionnaireAnswers).some((v) => v?.trim());
  const assetsComplete = inputAssets.some((a) => a?.uploadStatus === "uploaded" && a?.serverId) || questionnaireHasAnswers;
  const promptComplete = Boolean(normalizedAiPrompt || hasStructuredPromptSelections);
  const promptHasEnoughDetail = normalizedAiPrompt.length >= 20 || hasStructuredPromptSelections;
  const styleTemplateComplete = Boolean(styleComplete && templateComplete);
  const hasNameAndDomain = Boolean(detailsComplete);
  const hasUploadedFiles = Boolean(assetsComplete);
  const isReadyForStyleTemplate = hasUploadedFiles && promptHasEnoughDetail && !styleTemplateComplete;
  const isReadyForNameDomain = hasUploadedFiles && promptHasEnoughDetail && styleTemplateComplete && !hasNameAndDomain;
  const isReadyToCreate =
    hasUploadedFiles && promptHasEnoughDetail && styleTemplateComplete && hasNameAndDomain;
  const readinessItems = [
    { key: "details", label: "Identity", done: detailsComplete, required: true },
    { key: "assets", label: "Files", done: assetsComplete, required: false },
    { key: "prompt", label: "Outline", done: promptComplete, required: false },
    { key: "style_template", label: "Style/Template", done: styleTemplateComplete, required: true },
  ];
  const requiredStepsTotal = readinessItems.filter((item) => item.required).length;
  const requiredStepsComplete = readinessItems.filter(
    (item) => item.required && item.done
  ).length;
  const showTopBarSteps = windowWidth >= 1100;
  const stepGlowClass = (stepKey) => {
    const active = highlightedStepKey === stepKey;
    return `transition-shadow duration-700 ease-out ${
      active
        ? "ring-2 ring-green-400/70 shadow-[0_0_0_8px_rgba(74,222,128,0.22),0_0_26px_rgba(74,222,128,0.55)]"
        : "shadow-none"
    }`;
  };
  const selectedServerAssets = inputAssets.filter((a) => a?.serverId);
  const hasAnySelectedServerAssets = selectedServerAssets.length > 0;
  const hasPendingExtraction = selectedServerAssets.some((a) => (a?.extractionStatus || "pending") === "pending");
  const hasFailedExtraction = selectedServerAssets.some((a) => a?.extractionStatus === "failed");
  const hasIncompleteExtraction = selectedServerAssets.some((a) => (a?.extractionStatus || "pending") !== "completed");
  const createButtonDisabled =
    !canCreate || isCreating || isUploadingAssets || isGenerating || hasActiveUploads || hasIncompleteExtraction;
  const createButtonContent =
    isCreating || isUploadingAssets || isGenerating ? (
      <div className="flex items-center">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2"></div>
        {isGenerating ? "Generating Website..." : isUploadingAssets ? "Uploading Assets..." : "Creating Website..."}
      </div>
    ) : hasActiveUploads ? (
      "Uploading assets..."
    ) : (
      "Create Website"
    );
  const getFixedHeaderOffsetPx = useCallback(() => {
    if (!isStreamline) return 0;
    const navbarApproxPx = 64; // dashboard navbar height (matches top-16 offset used by streamline bar)
    const barPx = streamlineBarRef.current?.getBoundingClientRect?.().height ?? 0;
    const extraGapPx = 8;
    return navbarApproxPx + barPx + extraGapPx;
  }, [isStreamline]);

  const scrollElementToTop = useCallback(
    (el, behavior = "smooth") => {
      if (!el) return;

      if (isStreamline) {
        const offsetPx = getFixedHeaderOffsetPx();
        const targetTop = window.scrollY + el.getBoundingClientRect().top - offsetPx;
        window.scrollTo({ top: Math.max(0, targetTop), behavior });
        return;
      }

      el.scrollIntoView({ behavior, block: "start" });
    },
    [getFixedHeaderOffsetPx, isStreamline]
  );

  const highlightStep = useCallback((stepKey) => {
    const key = String(stepKey || "").trim();
    if (!key) return;
    setHighlightedStepKey(key);
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedStepKey(null);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  const handleTopBarStepClick = useCallback(
    (stepKey) => {
      const key = String(stepKey || "").trim();
      const ref =
        key === "details"
          ? detailsCardRef
          : key === "assets"
          ? assetsCardRef
          : key === "prompt"
          ? promptCardRef
          : key === "style_template"
          ? styleTemplateCardRef
          : null;

      if (ref?.current) {
        scrollElementToTop(ref.current, "smooth");
      }
      highlightStep(key);
    },
    [highlightStep, scrollElementToTop]
  );

  const handleStyleSelect = useCallback((style) => {
    setSelectedStyle(style);
  }, []);

  const handleStylePaginationChange = useCallback((info) => {
    const nextPage = Number(info?.currentPage ?? 0);
    const nextTotal = Number(info?.totalPages ?? 0);
    const nextExpanded = Boolean(info?.isExpanded);
    setStylePagination((prev) => {
      if (prev.currentPage === nextPage && prev.totalPages === nextTotal && prev.isExpanded === nextExpanded) return prev;
      return { currentPage: nextPage, totalPages: nextTotal, isExpanded: nextExpanded };
    });
  }, []);

  const handleTemplateSelect = useCallback((template) => {
    setSelectedTemplate(template);
  }, []);

  const loadStyleTemplateOptions = useCallback(async () => {
    setIsLoadingStyleTemplateOptions(true);
    setStyleTemplateOptionsError("");
    try {
      const [stylesResp, templatesResp] = await Promise.all([
        apiFetch("/api/style-presets/", { method: "GET" }),
        apiFetch("/api/website-templates/", { method: "GET" }),
      ]);

      if (!stylesResp.ok || !templatesResp.ok) {
        throw new Error("Failed to load styles/templates");
      }

      const [stylesData, templatesData] = await Promise.all([
        stylesResp.json().catch(() => null),
        templatesResp.json().catch(() => null),
      ]);

      setAvailableStyles(sortByPreferredOrder(stylesData, PREFERRED_STYLE_ORDER_MAP));
      setAvailableTemplates(
        sortByPreferredOrder(templatesData, PREFERRED_TEMPLATE_ORDER_MAP).map(
          normalizeCreateFlowTemplateOption
        )
      );
    } catch (e) {
      console.error("[CreateNewWebsite] failed to load styles/templates:", e);
      setAvailableStyles([]);
      setAvailableTemplates([]);
      setStyleTemplateOptionsError("network error");
    } finally {
      setIsLoadingStyleTemplateOptions(false);
    }
  }, []);

  useEffect(() => {
    loadStyleTemplateOptions();
  }, [loadStyleTemplateOptions]);

  useEffect(() => {
    if (!Array.isArray(availableTemplates) || availableTemplates.length === 0) return;

    setSelectedTemplate((prev) => {
      const currentValue = String(prev?.value || "").trim();
      if (currentValue) {
        const matchingCurrent = availableTemplates.find((template) => template?.value === currentValue);
        if (matchingCurrent) return matchingCurrent;
      }

      const defaultTemplate =
        availableTemplates.find((template) => template?.value === DEFAULT_TEMPLATE_VALUE) ||
        availableTemplates[0] ||
        null;

      return defaultTemplate;
    });
  }, [availableTemplates]);

  const previewCardLayout = "order-4 xl:col-span-12 xl:order-5";
  const detailsCardLayout = "order-1 xl:col-span-12 xl:order-2";
  const assetsCardLayout = "order-2 xl:col-span-6 xl:order-3";
  const promptCardLayout = "order-3 xl:col-span-6 xl:order-4";
  const previewCardClassName = `${surfaces.glassCard} rounded-3xl border ${borders.glass} p-4 shadow-sm`;
  const previewTitle = "Style and Template";
  const showPreviewDescription = false;
  const domainLabel = "Domain (auto-generated from name)";
  const postCreatePanel = postCreateMessage ? (
    <div className={`mt-4 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm rounded-xl border ${borders.glass} p-4`}>
      <div className={`${typography.body} ${textColors.primary}`}>{postCreateMessage}</div>

      {createdWebsiteInfo && (
        // When the error modal is open, avoid duplicating "Continue to Editor" actions behind it.
        <div className="mt-3 flex flex-wrap gap-2">
          {!isGenerationErrorModalOpen ? (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/edit/${username}/${createdWebsiteInfo.domain}/${createdWebsiteInfo.version_slug}?tutorial_prompt=1`
                )
              }
              className={`px-4 py-2 rounded-md ${typography.button} ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText}`}
            >
              Continue to Editor
            </button>
          ) : null}
          {!isGenerationErrorModalOpen &&
            generationError &&
            inputAssets.some((a) => a.uploadStatus === "uploaded" && a.serverId && a.extractionStatus === "completed") && (
              <button
                type="button"
                onClick={() => handleRetryGeneration()}
                className={`px-4 py-2 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.button} ${textColors.primary}`}
              >
                Retry Generation
              </button>
            )}
          {!isGenerationErrorModalOpen && inputAssets.some((a) => a.uploadStatus === "failed") && (
            <button
              type="button"
              onClick={() => handleRetryFailedUploads()}
              className={`px-4 py-2 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.button} ${textColors.primary}`}
            >
              Retry Failed Uploads
            </button>
          )}
        </div>
      )}
    </div>
  ) : null;

  const defaultCoachText =
    "Add your resume and profile picture (and more) to make a tailored website!";
  const extractionWaitCoachText =
    "We’re extracting text from your files. Please wait before creating your website.";
  const extractionFailedCoachText =
    "Some files failed extraction. Please retry extraction before creating your website.";
  const promptCoachText =
    "Tell us how you envision your website. What goes where? The more detail, the better.";
  const styleTemplateCoachText =
    "Pick a style and template so the preview matches your vision — then we can generate a cohesive site.";
  const nameDomainCoachText =
    "Give your website a name and choose your URL — this is what people will see and share.";
  const readyCoachText = "All set — your website is ready to generate. Hit “Create Website”.";

  const aiCoachText = (() => {
    // Hover-sensitive nudges (only override when the hovered area is incomplete / relevant)
    if (hoverHintArea === "assets" && !hasUploadedFiles) return defaultCoachText;
    if (hoverHintArea === "prompt" && hasUploadedFiles && !promptHasEnoughDetail) return promptCoachText;
    if (hoverHintArea === "preview" && isReadyForStyleTemplate) return styleTemplateCoachText;
    if (hoverHintArea === "details" && isReadyForNameDomain) return nameDomainCoachText;

    // Progress-based default
    if (!hasUploadedFiles) return defaultCoachText;
    if (hasAnySelectedServerAssets && hasPendingExtraction) return extractionWaitCoachText;
    if (hasAnySelectedServerAssets && hasFailedExtraction) return extractionFailedCoachText;
    if (hasUploadedFiles && !promptHasEnoughDetail) return promptCoachText;
    if (isReadyForStyleTemplate) return styleTemplateCoachText;
    if (isReadyForNameDomain) return nameDomainCoachText;
    if (isReadyToCreate) return readyCoachText;
    return defaultCoachText;
  })();

  const streamlinePromptPlaceholderShort =
    "Example: 1) Home: hero headline + 1 sentence summary, key achievements, CTA button. (Click to expand)";
  const streamlinePromptPlaceholderFull =
    "Example:\n1) Home: hero headline + 1 sentence summary, key achievements, CTA button.\n2) About: short bio, values, timeline.\n3) Experience: roles in reverse-chronological order, impact bullets.\n4) Projects: 4–6 projects (problem → solution → results), links.\n5) Publications/Awards: grouped by year.\n6) Contact: email + links.\n\nTone: professional, concise. Priority: projects + impact.";
  const nonStreamlinePromptPlaceholder =
    "Example: Build a professional academic portfolio. Emphasize research, publications, teaching, and awards. Keep tone concise and formal.";
  const handlePreviewNavigate = useCallback(
    (targetPath) => {
      if (!targetPath) return;
      let pathname = targetPath;
      try {
        const parsed = new URL(targetPath, window.location.origin);
        pathname = parsed.pathname || targetPath;
      } catch {
        pathname = targetPath;
      }
      const params = extractRouteParams(pathname);
      if (!params?.username || !params?.domain) return;
      if (
        params.username !== previewInfo?.username ||
        params.domain !== previewInfo?.domain
      ) {
        return;
      }
      setPreviewSubdomain(params.subdomain || "");
    },
    [previewInfo?.domain, previewInfo?.username],
  );

  const handlePreviewWebsiteReady = useCallback(() => {
    setPreviewWebsiteReady(true);
  }, []);

  const resetOutlineHelp = useCallback(() => {
    setOutlineHelpPrimaryGoal(null);
    setOutlineHelpPrimaryGoalOtherText("");
    setOutlineHelpTone(null);
    setOutlineHelpNarrateAsThirdPerson(OUTLINE_HELP_NARRATE_AS_THIRD_PERSON_DEFAULT);
    setOutlineHelpFormat(OUTLINE_HELP_FORMAT_DEFAULT);
    setOutlineHelpSections([]);
    setOutlineHelpTextLength(OUTLINE_HELP_TEXT_LENGTH_DEFAULT);
  }, []);

  const handleOutlineHelpPrimaryGoalSelect = useCallback((value) => {
    const next = String(value || "").trim();
    if (!next) return;
    setOutlineHelpPrimaryGoal((prev) => (prev === next ? null : next));
  }, []);

  const handleOutlineHelpToneSelect = useCallback((value) => {
    const next = String(value || "").trim();
    if (!next) return;
    setOutlineHelpTone((prev) => (prev === next ? null : next));
  }, []);

  const handleOutlineHelpNarrationSelect = useCallback((thirdPerson) => {
    setOutlineHelpNarrateAsThirdPerson(Boolean(thirdPerson));
  }, []);

  const handleOutlineHelpFormatChange = useCallback((e) => {
    const next = Number(e?.target?.value);
    if (!Number.isFinite(next)) return;
    setOutlineHelpFormat(next);
  }, []);

  const toggleOutlineHelpSection = useCallback((sectionValue) => {
    const key = String(sectionValue || "").trim();
    if (!key) return;
    setOutlineHelpSections((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  }, []);

  const handleOutlineHelpTextLengthChange = useCallback((e) => {
    const next = Number(e?.target?.value);
    if (!Number.isFinite(next)) return;
    setOutlineHelpTextLength(next);
  }, []);
  const normalizedLibrarySearchQuery = useExpandedLibraryPickerLayout
    ? String(librarySearchQuery || "").toLowerCase().trim()
    : "";
  const filteredCompletedExistingAssets = availableCompletedExistingAssets.filter((asset) => {
    if (!normalizedLibrarySearchQuery) return true;
    const filename = String(asset?.original_filename || `Asset #${asset?.id || ""}`).toLowerCase();
    return filename.includes(normalizedLibrarySearchQuery);
  });
  const hasLibrarySearchQuery = Boolean(normalizedLibrarySearchQuery);

  const handleApplyLibrarySearch = useCallback(() => {
    setLibrarySearchQuery(String(librarySearchInput || "").trim());
  }, [librarySearchInput]);

  const handleLibrarySearchKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      handleApplyLibrarySearch();
    },
    [handleApplyLibrarySearch]
  );

  const loadExistingAssets = useCallback(async () => {
    setIsLoadingExistingAssets(true);
    setExistingAssetsError("");
    try {
      const data = await listAIInputAssets();
      setExistingAssets(data?.results || []);
    } catch (e) {
      // Never surface raw network/stack messages (e.g. "Failed to fetch") to end-users.
      console.error("[CreateNewWebsite] failed to load existing assets:", e);
      setExistingAssetsError("Could not get your uploaded files");
    } finally {
      setIsLoadingExistingAssets(false);
    }
  }, []);
  // Load user's existing AI input assets (library) for reuse.
  useEffect(() => {
    loadExistingAssets();
  }, [loadExistingAssets]);

  const MAX_ASSET_SIZE_BYTES = 25 * 1024 * 1024;
  const ENABLE_EXTRACTION_POLLING = process.env.NODE_ENV !== "test";
  const EXTRACTION_POLL_INTERVAL_MS = 1500;

  const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(bytes)) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
      size /= 1024;
      idx += 1;
    }
    return `${size.toFixed(1)} ${units[idx]}`;
  };

  const makeLocalId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const getAssetStatusPresentation = (asset) => {
    const uploadStatus = asset?.uploadStatus;
    const extractionStatus = asset?.extractionStatus || (asset?.serverId ? "pending" : null);

    // Upload phase
    if (uploadStatus === "queued" || uploadStatus === "uploading") {
      return { label: "Uploading…", className: `${textColors.secondary}`, showCheck: false };
    }
    if (uploadStatus === "failed") {
      return { label: "Upload failed", className: "text-red-600 dark:text-red-400", showCheck: false };
    }

    // Uploaded phase
    if (uploadStatus === "uploaded") {
      if (extractionStatus === "completed") {
        return { label: "Uploaded — AI ready", className: "text-green-600 dark:text-green-400", showCheck: true };
      }
      if (extractionStatus === "failed") {
        return { label: "Uploaded — AI failed", className: "text-red-600 dark:text-red-400", showCheck: false };
      }
      // pending/null: treat as in-progress
      return { label: "Uploaded — AI reading…", className: "text-yellow-600 dark:text-yellow-400", showCheck: false };
    }

    // Fallback
    return { label: String(uploadStatus || "—"), className: `${textColors.secondary}`, showCheck: false };
  };

  const addFilesToQueue = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;

    const newAssets = arr.map((file) => ({
      localId: makeLocalId(),
      source: "upload",
      file,
      assetType: file.type && file.type.startsWith("image/") ? "image" : "file",
      comment: "",
      lastSavedComment: "",
      uploadAutoRetryCount: 0,
      extractionAutoRetryCount: 0,
      extractedTextUser: "",
      lastSavedExtractedTextUser: "",
      extractedTextUserDraft: "",
      isEditingComment: false,
      isSavingComment: false,
      commentSaveError: "",
      uploadStatus: file?.size && file.size > MAX_ASSET_SIZE_BYTES ? "failed" : "queued", // queued|uploading|uploaded|failed
      progress: 0,
      serverId: null,
      uploadError: file?.size && file.size > MAX_ASSET_SIZE_BYTES ? "File too large (max 25MB)" : "",
      extractionStatus: null, // null|pending|completed|failed
      extractionError: "",
      extractedTextPreview: "",
      isExtractedPreviewExpanded: false,
    }));

    setInputAssets((prev) => [...prev, ...newAssets]);

    // Auto-upload immediately (even before website is created).
    // If website exists, attach directly; otherwise upload without website_id and attach on create.
    newAssets.forEach((a) => {
      if (a.uploadStatus === "queued") {
        uploadSingleAsset(a.localId, { file: a.file, assetType: a.assetType, comment: a.comment });
      }
    });
  };

  const uploadSingleAsset = async (localId, snapshot = null, { websiteIdOverride } = {}) => {
    const localIdStr = String(localId || "");
    if (!localIdStr) return false;
    if (uploadInFlightRef.current.has(localIdStr)) return false;
    uploadInFlightRef.current.add(localIdStr);

    // Prefer snapshot (used for immediate auto-upload right after selecting files),
    // otherwise fall back to current state (used for retry).
    const fileToUpload = snapshot?.file || inputAssets.find((a) => a.localId === localId)?.file;
    const assetType = snapshot?.assetType || inputAssets.find((a) => a.localId === localId)?.assetType || "file";
    const comment = snapshot?.comment ?? inputAssets.find((a) => a.localId === localId)?.comment ?? "";

    setInputAssets((prev) =>
      prev.map((x) =>
        x.localId === localId
          ? { ...x, uploadStatus: "uploading", progress: 0, uploadError: "", extractionStatus: null, extractionError: "" }
          : x
      )
    );

    if (!fileToUpload) {
      setInputAssets((prev) =>
        prev.map((x) => (x.localId === localId ? { ...x, uploadStatus: "failed", uploadError: "Missing file" } : x))
      );
      uploadInFlightRef.current.delete(localIdStr);
      return false;
    }

    if (fileToUpload?.size && fileToUpload.size > MAX_ASSET_SIZE_BYTES) {
      setInputAssets((prev) =>
        prev.map((x) =>
          x.localId === localId
            ? { ...x, uploadStatus: "failed", uploadError: "File too large (max 25MB)" }
            : x
        )
      );
      uploadInFlightRef.current.delete(localIdStr);
      return false;
    }

    try {
      const websiteId =
        websiteIdOverride != null ? websiteIdOverride : createdWebsiteInfo?.website_id || null;
      let lastError = null;

      for (let attempt = 0; attempt <= UPLOAD_AUTO_RETRY_MAX_RETRIES; attempt += 1) {
        try {
          if (attempt > 0) {
            setInputAssets((prev) =>
              prev.map((x) =>
                x.localId === localId
                  ? {
                      ...x,
                      uploadStatus: "uploading",
                      progress: 0,
                      uploadError: "",
                      uploadAutoRetryCount: (x.uploadAutoRetryCount || 0) + 1,
                    }
                  : x
              )
            );
            if (UPLOAD_AUTO_RETRY_DELAY_MS) {
              await sleep(UPLOAD_AUTO_RETRY_DELAY_MS);
            }
          } else {
            // reset auto retry count for a fresh upload attempt
            setInputAssets((prev) =>
              prev.map((x) =>
                x.localId === localId ? { ...x, uploadAutoRetryCount: 0, extractionAutoRetryCount: 0 } : x
              )
            );
          }

          if (unmountedRef.current) {
            uploadInFlightRef.current.delete(localIdStr);
            return false;
          }

          const result = await Promise.race([
            uploadAIInputAsset({
              websiteId,
              file: fileToUpload,
              assetType,
              comment,
              onProgress: (pct) => {
                setInputAssets((prev) => prev.map((x) => (x.localId === localId ? { ...x, progress: pct } : x)));
              },
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Upload timed out")), UPLOAD_TIMEOUT_MS)
            ),
          ]);

          const serverAsset = result?.asset;
          if (!serverAsset?.id) {
            throw new Error("Upload succeeded but server returned incomplete data");
          }
          const persistedComment = (serverAsset?.comment ?? comment ?? "").trim();
          const persistedExtractedTextUser = serverAsset?.extracted_text_user ?? "";
          setInputAssets((prev) =>
            prev.map((x) =>
              x.localId === localId
                ? {
                    ...x,
                    uploadStatus: "uploaded",
                    progress: 100,
                    serverId: serverAsset?.id ?? x.serverId,
                    uploadError: "",
                    extractionStatus: serverAsset?.status || "pending",
                    extractionError: "",
                    extractionAutoRetryCount: 0, // new extraction attempt starts after upload
                    extractedTextPreview: serverAsset?.extracted_text || x.extractedTextPreview || "",
                    comment: persistedComment,
                    lastSavedComment: persistedComment,
                    extractedTextUser: persistedExtractedTextUser,
                    lastSavedExtractedTextUser: persistedExtractedTextUser,
                    extractedTextUserDraft:
                      persistedExtractedTextUser || (serverAsset?.extracted_text || x.extractedTextPreview || ""),
                    isExtractedPreviewExpanded: x.isExtractedPreviewExpanded ?? false,
                    isSavingComment: false,
                    commentSaveError: "",
                  }
                : x
            )
          );
          if (ENABLE_EXTRACTION_POLLING && serverAsset?.id) {
            pollExtractionStatus(serverAsset.id);
          }

          uploadInFlightRef.current.delete(localIdStr);
          return true;
        } catch (e) {
          lastError = e;
          const canRetry =
            attempt < UPLOAD_AUTO_RETRY_MAX_RETRIES &&
            shouldAutoRetryUploadError(e) &&
            !unmountedRef.current;
          if (canRetry) {
            continue;
          }
          break;
        }
      }

      setInputAssets((prev) =>
        prev.map((x) =>
          x.localId === localId
            ? { ...x, uploadStatus: "failed", uploadError: lastError?.message || "Upload failed" }
            : x
        )
      );
      uploadInFlightRef.current.delete(localIdStr);
      return false;
    } finally {
      uploadInFlightRef.current.delete(localIdStr);
    }
  };

  const handlePickFiles = () => filePickerRef.current?.click?.();

  const handleFileInputChange = (e) => {
    addFilesToQueue(e.target.files);
    // allow picking the same file again
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files) addFilesToQueue(e.dataTransfer.files);
  };

  const handleRemoveAsset = async (localId) => {
    const asset = inputAssets.find((a) => a.localId === localId);
    if (!asset) return;

    // If already uploaded, delete from backend as well.
    if (asset.serverId) {
      const idStr = String(asset.serverId);
      // Stop polling if present.
      const t = extractionPollersRef.current[idStr];
      if (t) {
        clearTimeout(t);
        delete extractionPollersRef.current[idStr];
      }
      try {
        autoExtractionRetryDoneRef.current.delete(idStr);
        extractionRetryInFlightRef.current.delete(idStr);
        uploadInFlightRef.current.delete(String(localId));
      } catch (e) {
        // no-op
      }
      try {
        // If we already created the website, "remove" means detach from that website (keep in library).
        if (createdWebsiteInfo?.website_id) {
          await detachAIInputAssetFromWebsite(asset.serverId, createdWebsiteInfo.website_id);
        } else if (asset.source !== "library") {
          await deleteAIInputAsset(asset.serverId);
        }
      } catch (e) {
        // best-effort; keep removing locally
      }
    }
    setInputAssets((prev) => prev.filter((a) => a.localId !== localId));
  };

  const handleRetryUpload = async (localId) => {
    const asset = inputAssets.find((a) => a.localId === localId);
    if (!asset) return;
    if (asset.file?.size && asset.file.size > MAX_ASSET_SIZE_BYTES) return;
    await uploadSingleAsset(localId);
  };

  const addExistingAssetToSelection = (asset) => {
    if (!asset?.id) return;
    const serverId = asset.id;
    setInputAssets((prev) => {
      const alreadySelected = prev.some((x) => x?.source === "library" && x?.serverId === serverId);
      if (alreadySelected) return prev;
      const extractedText = asset?.extracted_text || "";
      const extractedTextUser = asset?.extracted_text_user || "";
      const initialDraft = extractedTextUser || extractedText || "";
      return [
        ...prev,
        {
          localId: `lib-${serverId}`,
          source: "library",
          file: null,
          originalFilename: asset?.original_filename || `Asset #${serverId}`,
          assetType: asset?.asset_type === "image" ? "image" : "file",
          uploadAutoRetryCount: 0,
          extractionAutoRetryCount: 0,
          comment: (asset?.comment || "").trim(),
          lastSavedComment: (asset?.comment || "").trim(),
          extractedTextUser,
          lastSavedExtractedTextUser: extractedTextUser,
          extractedTextUserDraft: initialDraft,
          uploadStatus: "uploaded",
          progress: 100,
          serverId,
          uploadError: "",
          extractionStatus: asset?.status || "completed",
          extractionError: "",
          extractedTextPreview: extractedText,
          isExtractedPreviewExpanded: false,
          isEditingComment: false,
          isSavingComment: false,
          commentSaveError: "",
        },
      ];
    });
  };

  const handleAssetCommentChange = (localId, newComment) => {
    setInputAssets((prev) =>
      prev.map((a) => (a.localId === localId ? { ...a, comment: newComment, commentSaveError: "" } : a))
    );
  };

  const startEditLocalComment = (localId) => {
    setInputAssets((prev) =>
      prev.map((a) =>
        a.localId === localId
          ? {
              ...a,
              isEditingComment: true,
              extractedTextUserDraft:
                a.extractedTextUserDraft ??
                (a.extractedTextUser || a.extractedTextPreview || ""),
            }
          : a
      )
    );
  };

  const cancelEditLocalComment = (localId) => {
    setInputAssets((prev) =>
      prev.map((a) =>
        a.localId === localId
          ? {
              ...a,
              isEditingComment: false,
              comment: a.lastSavedComment ?? a.comment ?? "",
              extractedTextUserDraft:
                a.lastSavedExtractedTextUser ?? a.extractedTextUserDraft ?? "",
              commentSaveError: "",
              isSavingComment: false,
            }
          : a
      )
    );
  };

  const persistLocalComment = async (localId) => {
    const asset = inputAssets.find((a) => a.localId === localId);
    if (!asset) return;

    const serverId = asset.serverId;
    const nextComment = (asset.comment ?? "").trim();
    const nextExtractedTextUser = asset.extractedTextUserDraft ?? "";
    const lastSavedComment = (asset.lastSavedComment ?? "").trim();
    const lastSavedExtractedTextUser = asset.lastSavedExtractedTextUser ?? "";

    // Not uploaded yet: keep local (will be included on upload); no saved check until persisted.
    if (!serverId) {
      setInputAssets((prev) =>
        prev.map((a) =>
          a.localId === localId ? { ...a, isEditingComment: false, comment: nextComment } : a
        )
      );
      return;
    }

    // No changes.
    if (nextComment === lastSavedComment && nextExtractedTextUser === lastSavedExtractedTextUser) {
      setInputAssets((prev) => prev.map((a) => (a.localId === localId ? { ...a, isEditingComment: false } : a)));
      return;
    }

    setInputAssets((prev) =>
      prev.map((a) =>
        a.localId === localId ? { ...a, isSavingComment: true, commentSaveError: "" } : a
      )
    );

    try {
      const result = await updateAIInputAsset(serverId, { comment: nextComment, extractedTextUser: nextExtractedTextUser });
      const serverAsset = result?.asset;
      const persistedComment = (serverAsset?.comment ?? nextComment ?? "").trim();
      const persistedExtractedTextUser = serverAsset?.extracted_text_user ?? nextExtractedTextUser ?? "";
      setInputAssets((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? {
                ...a,
                comment: persistedComment,
                lastSavedComment: persistedComment,
                extractedTextUser: persistedExtractedTextUser,
                lastSavedExtractedTextUser: persistedExtractedTextUser,
                extractedTextUserDraft: persistedExtractedTextUser,
                isEditingComment: false,
                isSavingComment: false,
                commentSaveError: "",
              }
            : a
        )
      );
    } catch (e) {
      setInputAssets((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? {
                ...a,
                isSavingComment: false,
                commentSaveError: e?.message || "Failed to save comment",
                isEditingComment: true,
              }
            : a
        )
      );
    }
  };

  const uploadQueuedOrFailedAssets = async (websiteId) => {
    const uploadable = inputAssets.filter((a) => a.uploadStatus === "queued" || a.uploadStatus === "failed");
    if (!uploadable.length) return 0;

    let failedCount = 0;
    setIsUploadingAssets(true);
    setPostCreateMessage("Uploading your assets…");

    for (const asset of uploadable) {
      // Ensure websiteId is used even if `createdWebsiteInfo` hasn't propagated to state yet.
      const ok = await uploadSingleAsset(
        asset.localId,
        { file: asset.file, assetType: asset.assetType, comment: asset.comment },
        { websiteIdOverride: websiteId }
      );
      if (!ok) failedCount += 1;
    }

    setIsUploadingAssets(false);
    return failedCount;
  };

  const pollExtractionStatus = async (assetId) => {
    const idStr = String(assetId);
    // Avoid multiple pollers for same asset.
    if (extractionPollersRef.current[idStr]) return;

    const tick = async () => {
      try {
        const data = await getAIInputAsset(assetId);
        const serverAsset = data?.asset;
        if (!serverAsset) throw new Error("Invalid server response");

        setInputAssets((prev) =>
          prev.map((a) =>
            a.serverId === assetId
              ? (() => {
                  const serverExtractedUser = serverAsset.extracted_text_user ?? a.extractedTextUser ?? "";
                  const nextRaw = serverAsset.extracted_text || a.extractedTextPreview || "";
                  const shouldUpdateDraft = !a.isEditingComment;
                  return {
                    ...a,
                    extractionStatus: serverAsset.status,
                    extractionError: "",
                    extractedTextPreview: nextRaw,
                    extractedTextUser: serverExtractedUser,
                    lastSavedExtractedTextUser: serverExtractedUser,
                    extractedTextUserDraft: shouldUpdateDraft ? (serverExtractedUser || nextRaw) : a.extractedTextUserDraft,
                    isExtractedPreviewExpanded: a.isExtractedPreviewExpanded ?? false,
                  };
                })()
              : a
          )
        );

        // Automatic extraction retry ONCE (common transient backend failures).
        if (
          serverAsset.status === "failed" &&
          !autoExtractionRetryDoneRef.current.has(idStr) &&
          !unmountedRef.current
        ) {
          autoExtractionRetryDoneRef.current.add(idStr);
          // Mark in UI (optional debug signal).
          setInputAssets((prev) =>
            prev.map((a) =>
              a.serverId === assetId ? { ...a, extractionAutoRetryCount: (a.extractionAutoRetryCount || 0) + 1 } : a
            )
          );

          // Stop this poller before restarting via `handleRetryExtraction`.
          const t = extractionPollersRef.current[idStr];
          if (t) clearTimeout(t);
          delete extractionPollersRef.current[idStr];

          if (EXTRACTION_AUTO_RETRY_DELAY_MS) {
            await sleep(EXTRACTION_AUTO_RETRY_DELAY_MS);
          }
          if (unmountedRef.current) return;
          await handleRetryExtraction(assetId, { isAuto: true });
          return;
        }

        if (serverAsset.status === "completed" || serverAsset.status === "failed") {
          const t = extractionPollersRef.current[idStr];
          if (t) clearTimeout(t);
          delete extractionPollersRef.current[idStr];
          return;
        }
      } catch (e) {
        // Network hiccup: keep polling, but record a soft error (do not flip to failed).
        setInputAssets((prev) =>
          prev.map((a) =>
          a
          )
        );
      }

      extractionPollersRef.current[idStr] = setTimeout(tick, EXTRACTION_POLL_INTERVAL_MS);
    };

    extractionPollersRef.current[idStr] = setTimeout(tick, EXTRACTION_POLL_INTERVAL_MS);
  };

  const handleRetryExtraction = async (serverId, { isAuto = false } = {}) => {
    if (!serverId) return;
    const idStr = String(serverId);
    if (extractionRetryInFlightRef.current.has(idStr)) return;
    extractionRetryInFlightRef.current.add(idStr);

    if (isAuto) {
      // Optional UI/debug signal: track that we already auto-retried once.
      setInputAssets((prev) =>
        prev.map((a) =>
          a.serverId === serverId
            ? { ...a, extractionAutoRetryCount: Math.max(1, a.extractionAutoRetryCount || 0) }
            : a
        )
      );
    }
    setInputAssets((prev) =>
      prev.map((a) =>
        a.serverId === serverId ? { ...a, extractionStatus: "pending", extractionError: "" } : a
      )
    );
    try {
      const data = await retryAIInputAssetExtraction(serverId);
      const serverAsset = data?.asset;
      setInputAssets((prev) =>
        prev.map((a) =>
          a.serverId === serverId
            ? {
                ...a,
                extractionStatus: serverAsset?.status || "pending",
                extractionError: "",
                extractedTextPreview: serverAsset?.extracted_text || a.extractedTextPreview || "",
                extractedTextUser: serverAsset?.extracted_text_user ?? a.extractedTextUser ?? "",
                lastSavedExtractedTextUser: serverAsset?.extracted_text_user ?? a.lastSavedExtractedTextUser ?? "",
                extractedTextUserDraft:
                  !a.isEditingComment
                    ? (serverAsset?.extracted_text_user ?? "") || (serverAsset?.extracted_text || a.extractedTextPreview || "")
                    : a.extractedTextUserDraft,
              }
            : a
        )
      );
      if (ENABLE_EXTRACTION_POLLING) pollExtractionStatus(serverId);
    } catch (e) {
      setInputAssets((prev) =>
        prev.map((a) =>
          a.serverId === serverId ? { ...a, extractionStatus: "failed", extractionError: "" } : a
        )
      );
    } finally {
      extractionRetryInFlightRef.current.delete(idStr);
    }
  };

  // If extraction ever flips to "failed" (via polling or an immediate upload response),
  // automatically retry ONCE per asset, then fall back to the manual Retry button.
  useEffect(() => {
    if (!inputAssets?.length) return;
    const failed = inputAssets.filter(
      (a) => a?.serverId && String(a.extractionStatus || "").toLowerCase() === "failed"
    );
    if (!failed.length) return;

    failed.forEach((a) => {
      const idStr = String(a.serverId);
      if (!idStr) return;
      if (autoExtractionRetryDoneRef.current.has(idStr)) return;
      if (extractionRetryInFlightRef.current.has(idStr)) return;

      autoExtractionRetryDoneRef.current.add(idStr);
      (async () => {
        if (unmountedRef.current) return;
        if (EXTRACTION_AUTO_RETRY_DELAY_MS) {
          await sleep(EXTRACTION_AUTO_RETRY_DELAY_MS);
        }
        if (unmountedRef.current) return;
        await handleRetryExtraction(a.serverId, { isAuto: true });
      })();
    });
  }, [inputAssets]);

  // Function to convert name to URL-friendly domain
  const nameToUrlFriendly = (name) => normalizeSubdomain(name);

  // Handle domain input focus when field is not empty
  const handleDomainFocus = () => {
    if (domain.trim()) {
      setShouldAutoUpdateDomain(false);
    }
  };

  // Handle domain input change
  const handleDomainChange = (e) => {
    setDomain(e.target.value);
    setShouldAutoUpdateDomain(false);
  };

  // Handle manual domain generation from click
  const handleGenerateDomain = () => {
    if (websiteName.trim()) {
      setDomain(nameToUrlFriendly(websiteName));
      setShouldAutoUpdateDomain(false); // Stop auto-updating after manual generation
    }
  };

  // Validation functions (case-insensitive to match backend __iexact checks)
  const validateName = (name) => {
    if (!name.trim()) {
      return { isValid: true, message: "" };
    }

    const lowerName = name.trim().toLowerCase();
    if (existingNames.some((n) => n.toLowerCase() === lowerName)) {
      return { isValid: false, message: "This website name is already taken" };
    }

    return { isValid: true, message: "" };
  };

  const validateDomain = (domainValue) => {
    if (!domainValue.trim()) {
      return { isValid: true, message: "" };
    }

    const lowerDomain = domainValue.trim().toLowerCase();
    if (existingDomains.some((d) => d.toLowerCase() === lowerDomain)) {
      return { isValid: false, message: "This domain is already taken" };
    }

    return { isValid: true, message: "" };
  };

  // Load existing names and domains on component mount
  useEffect(() => {
    const fetchExistingNamesAndDomains = async () => {
      try {
        const response = await apiFetch("/api/websites/existing-names-domains/");
        if (response.ok) {
          const data = await response.json();
          setExistingNames(data.names);
          setExistingDomains(data.domains);
        }
      } catch (error) {
        console.error("Error fetching existing names and domains:", error);
      }
    };

    fetchExistingNamesAndDomains();
  }, []);

  // Validate name when it changes
  useEffect(() => {
    setNameValidation(validateName(websiteName));
  }, [websiteName, existingNames]);

  // Validate domain when it changes
  useEffect(() => {
    setDomainValidation(validateDomain(domain));
  }, [domain, existingDomains]);

  // Auto-update domain when website name changes (if allowed)
  useEffect(() => {
    if (websiteName.trim() && shouldAutoUpdateDomain) {
      setDomain(nameToUrlFriendly(websiteName));
    }
  }, [websiteName, shouldAutoUpdateDomain]);

  // Preview loader: ensure persisted preview + load pages
  useEffect(() => {
    const templateValue = selectedTemplate?.value || "navbar-navigation";
    const styleValue = selectedStyle?.value || null;

    if (!styleValue) {
      setPreviewInfo(null);
      setPreviewPages([]);
      setPreviewSubdomain("");
      setPreviewError("");
      setIsPreviewLoading(false);
      return undefined;
    }

    const requestId = ++previewRequestIdRef.current;
    const controller = new AbortController();

    if (previewEnsureTimerRef.current) {
      clearTimeout(previewEnsureTimerRef.current);
    }

    setIsPreviewLoading(true);
    setPreviewError("");
    setPreviewInfo(null);
    setPreviewScreenshotFailed(false);
    setPreviewWebsiteReady(false);
    setIsLivePreviewRevealed(false);

    previewEnsureTimerRef.current = setTimeout(async () => {
      try {
        const ensureOptions = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ template: templateValue, style: styleValue }),
        };
        if (process.env.NODE_ENV !== "test") {
          ensureOptions.signal = controller.signal;
        }

        const resp = await apiFetch("/api/templates/ensure-website-preview/", ensureOptions);

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          if (requestId === previewRequestIdRef.current) {
            setPreviewError(err?.error || "Failed to load preview");
            setPreviewInfo(null);
          }
          return;
        }

        const data = await resp.json();
        if (requestId !== previewRequestIdRef.current) return;
        setPreviewInfo(data);
      } catch (e) {
        if (e?.name !== "AbortError" && requestId === previewRequestIdRef.current) {
          setPreviewError("Network error loading preview");
          setPreviewInfo(null);
        }
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setIsPreviewLoading(false);
        }
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      controller.abort();
      if (previewEnsureTimerRef.current) {
        clearTimeout(previewEnsureTimerRef.current);
      }
    };
  }, [selectedTemplate, selectedStyle, previewRetryNonce]);

  useEffect(() => {
    const websiteId = previewInfo?.website_id;
    if (!websiteId) {
      setPreviewPages([]);
      setPreviewSubdomain("");
      setIsPreviewPagesLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const loadPages = async () => {
      try {
        setIsPreviewPagesLoading(true);
        const pageOptions = { method: "GET" };
        if (process.env.NODE_ENV !== "test") {
          pageOptions.signal = controller.signal;
        }
        const resp = await apiFetch(`/api/websites/${websiteId}/pages/`, pageOptions);
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          setPreviewError(err?.error || "Failed to load preview pages");
          setPreviewPages([]);
          return;
        }
        const data = await resp.json();
        const pages = Array.isArray(data) ? data : [];
        setPreviewPages(pages);
        setPreviewSubdomain((prev) => {
          const normalizedPrev = prev || "";
          const hasPrev = pages.some((p) => (p.subdomain || "") === normalizedPrev);
          if (hasPrev) return normalizedPrev;
          const fallback = pages.find((p) => !p.subdomain) || pages[0];
          return fallback?.subdomain || "";
        });
      } catch (e) {
        if (e?.name !== "AbortError") {
          setPreviewError("Network error loading preview pages");
          setPreviewPages([]);
        }
      } finally {
        setIsPreviewPagesLoading(false);
      }
    };

    loadPages();
    return () => controller.abort();
  }, [previewInfo?.website_id]);

  const startWebsiteGenerationJob = useCallback(
    async ({ websiteId, aiInputAssetIds, aiPrompt: promptForJob, templateName = null, generateAiImages = false, useProfileForImages = false, silent = false }) => {
      try {
        const resp = await apiFetch("/api/ai/website-generation/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            website_id: websiteId,
            ai_input_asset_ids: aiInputAssetIds,
            ai_prompt: promptForJob ?? null,
            template: templateName ?? null,
            generate_ai_images: generateAiImages,
            use_profile_for_images: useProfileForImages,
          }),
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          const msg = data?.error || "Failed to start generation";
          throw new Error(`${msg} (${resp.status})`);
        }
        const job = data?.job;
        if (!job?.id) throw new Error("Invalid job response");
        setGenerationStatus(job.status || "queued");
        return job.id;
      } catch (e) {
        // Keep technical details for debugging but never show to user directly.
        console.error("[CreateNewWebsite] website generation start failed:", e);
        if (!silent) {
          setIsGenerating(false);
          setGenerationJobId(null);
          setGenerationStatus("failed");
          setGenerationError(e?.message || "Generation failed. Please try again later.");
          setPostCreateMessage("Generation failed. Please try again later.");
          setIsGenerationErrorModalOpen(true);
        }
        throw e;
      }
    },
    // All deps are stable: state setters never change identity; apiFetch is module-level.
    []
  );

  const showGenerationStartFailure = useCallback((e) => {
    console.error("[CreateNewWebsite] generation start failed after retries:", e);
    setIsGenerating(false);
    setGenerationJobId(null);
    setGenerationStatus("failed");
    setGenerationError("Generation failed. Please try again later.");
    setPostCreateMessage("Generation failed. Please try again later.");
    setIsGenerationErrorModalOpen(true);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification("Website generation failed", { body: "Something went wrong. Tap to review and retry." }); } catch (_) {}
    }
  }, []);

  const startWebsiteGenerationJobWithAutoRetry = useCallback(
    async ({ websiteId, aiInputAssetIds, aiPrompt: promptForJob, templateName = null, generateAiImages = false, useProfileForImages = false }) => {
      // Persist params for a potential silent retry on early job failure.
      generationStartParamsRef.current = {
        websiteId,
        aiInputAssetIds,
        aiPrompt: promptForJob ?? null,
        templateName: templateName ?? null,
        generateAiImages,
        useProfileForImages,
      };

      // Allow one silent retry for start per (re)start attempt.
      generationAutoRetryUsedRef.current.start = false;

      try {
        return await startWebsiteGenerationJob({
          websiteId,
          aiInputAssetIds,
          aiPrompt: promptForJob,
          templateName,
          generateAiImages,
          useProfileForImages,
          silent: true,
        });
      } catch (e) {
        const canRetry =
          !generationAutoRetryUsedRef.current.start &&
          shouldAutoRetryGenerationError(e) &&
          !unmountedRef.current;
        if (!canRetry) throw e;

        generationAutoRetryUsedRef.current.start = true;
        if (GENERATION_START_AUTO_RETRY_DELAY_MS) {
          await sleep(GENERATION_START_AUTO_RETRY_DELAY_MS);
        }
        return await startWebsiteGenerationJob({
          websiteId,
          aiInputAssetIds,
          aiPrompt: promptForJob,
          templateName,
          generateAiImages,
          useProfileForImages,
          silent: true,
        });
      }
    },
    [startWebsiteGenerationJob]
  );

  const stopGenerationPolling = useCallback(() => {
    if (generationPollerRef.current) {
      clearTimeout(generationPollerRef.current);
      generationPollerRef.current = null;
    }
  }, []);

  const failGeneration = useCallback(
    (message) => {
      // Always fail in a user-visible way (modal + post-create panel), and stop polling.
      stopGenerationPolling();
      setIsGenerating(false);
      setGenerationStatus("failed");
      setGenerationError(message || "Generation failed. Please try again later.");
      setPostCreateMessage("Generation failed. Please try again later.");
      setIsGenerationErrorModalOpen(true);
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try { new Notification("Website generation failed", { body: "Something went wrong. Tap to review and retry." }); } catch (_) {}
      }
    },
    // stopGenerationPolling is stable (function declaration), safe to omit from deps in this file style,
    // but include it for correctness.
    [stopGenerationPolling]
  );

  const handleCreateWebsite = async () => {
    if (!canCreate) return;
    if (hasIncompleteExtraction) return;
    
    setIsCreating(true);
    setPostCreateMessage("");
    setGenerationError("");
    try {
      const payload = {
        name: websiteName.trim(),
        domain: domain.trim(),
        template: selectedTemplate.value,
        style_preset: selectedStyle.value,
        ai_prompt: normalizedAiPrompt ? normalizedAiPrompt : null,
        ai_input_asset_ids: Array.from(
          new Set([
            ...inputAssets.filter((a) => a.uploadStatus === "uploaded" && a.serverId).map((a) => a.serverId),
          ])
        ),
        generate_ai_images: true,
        use_profile_for_images: hasUploadedImage && useProfilePictureForProfessionalImages,
        // Structured profile fields (step-three outline-help answers)
        profile_primary_goal:
          outlineHelpPrimaryGoal === "other"
            ? outlineHelpPrimaryGoalOtherText.trim() || null
            : outlineHelpPrimaryGoal || null,
        profile_tone: outlineHelpTone || null,
        profile_narrate_as_third_person: Boolean(outlineHelpNarrateAsThirdPerson),
        profile_content_format: outlineHelpFormat,
        profile_content_length: outlineHelpTextLength,
        profile_content_sections: outlineHelpSections,
        profile_additional_instructions: (() => {
          const questionnaireText = CV_QUESTIONNAIRE_QUESTIONS
            .filter((q) => questionnaireAnswers[q.key]?.trim())
            .map((q) => `${q.label}\n${questionnaireAnswers[q.key].trim()}`)
            .join("\n\n");
          const parts = [normalizedAiPrompt, questionnaireText].filter(Boolean);
          return parts.length ? parts.join("\n\n---\n\n") : null;
        })(),
      };

      console.log("Creating website with:", payload);

      const response = await apiFetch("/api/websites/create/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Website created successfully:", data);
        setCreatedWebsiteInfo(data);

        const failedCount = await uploadQueuedOrFailedAssets(data.website_id);
        if (failedCount > 0) {
          setPostCreateMessage(
            `Website created. ${failedCount} asset${failedCount === 1 ? "" : "s"} failed to upload — you can retry or continue to the editor.`
          );
          return;
        }

        // If there are no selected assets, fall back to the existing behavior (template+style only).
        if (!payload.ai_input_asset_ids?.length) {
          navigate(`/edit/${username}/${data.domain}/${data.version_slug}?tutorial_prompt=1`);
          return;
        }

        // Start generation job and poll until complete/failed.
        setIsGenerating(true);
        setGenerationStatus("queued");
        generationSuccessSoundPlayedRef.current = false;
        generationStartedAtRef.current = Date.now();
        generationAutoRetryUsedRef.current = { start: false, earlyFailure: false };
        const templateNameForGeneration = payload.template;
        // Persist params immediately so early-failure retry logic always has them,
        // even if a subsequent state update or request error occurs.
        generationStartParamsRef.current = {
          websiteId: data.website_id,
          aiInputAssetIds: payload.ai_input_asset_ids || [],
          aiPrompt: payload.ai_prompt ?? null,
          templateName: templateNameForGeneration ?? null,
          generateAiImages: payload.generate_ai_images ?? false,
          useProfileForImages: payload.use_profile_for_images ?? false,
        };
        try {
          const jobId = await startWebsiteGenerationJobWithAutoRetry({
            websiteId: data.website_id,
            aiInputAssetIds: payload.ai_input_asset_ids,
            aiPrompt: payload.ai_prompt,
            templateName: templateNameForGeneration,
            generateAiImages: payload.generate_ai_images ?? false,
            useProfileForImages: payload.use_profile_for_images ?? false,
          });
          setGenerationJobId(jobId);
        } catch (e) {
          showGenerationStartFailure(e);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error creating website:", errorData);

        // Subscription gate: open upgrade modal (no browser alerts).
        if (errorData?.code === "WEBSITE_LIMIT_REACHED") {
          setCreateLimitInfo({
            plan_display_name:
              errorData?.plan?.display_name || errorData?.plan?.name || "your plan",
            max_websites: errorData?.plan?.max_websites,
            current_websites: errorData?.usage?.current_websites,
          });
          setPostCreateMessage("You’ve reached your website draft limit.");
          setUpgradeModalOpen(true);
          return;
        }

        // Backend name/domain uniqueness conflict — surface inline errors and refresh the stale list.
        if (errorData?.details) {
          if (errorData.details.name) {
            setNameValidation({
              isValid: false,
              message: errorData.details.name[0] || "This website name is already taken",
            });
          }
          if (errorData.details.domain) {
            setDomainValidation({
              isValid: false,
              message: errorData.details.domain[0] || "This domain is already taken",
            });
          }
          try {
            const refreshResp = await apiFetch("/api/websites/existing-names-domains/");
            if (refreshResp.ok) {
              const refreshData = await refreshResp.json();
              setExistingNames(refreshData.names || []);
              setExistingDomains(refreshData.domains || []);
            }
          } catch (_ignored) {
            // best-effort refresh
          }
          return;
        }

        setPostCreateMessage("We couldn’t create your website right now. Please try again.");
      }
    } catch (error) {
      console.error("Network error creating website:", error);
      setPostCreateMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (!isGenerating || !generationJobId) return;

    stopGenerationPolling();
    const startedAtRaw = Number(generationStartedAtRef.current);
    const startedAt = Number.isFinite(startedAtRaw) ? startedAtRaw : Date.now();
    generationConsecutivePollErrorsRef.current = 0;

    let cancelled = false;
    const controller = new AbortController();

    const poll = async () => {
      try {
        if (cancelled) return;
        const elapsed = Date.now() - startedAt;
        if (elapsed > GENERATION_POLL_TIMEOUT_MS) {
          failGeneration("Generation is taking longer than expected. Please try again later.");
          return;
        }

        const pollOptions = { method: "GET" };
        if (process.env.NODE_ENV !== "test") {
          pollOptions.signal = controller.signal;
        }
        const resp = await apiFetch(`/api/ai/website-generation/${generationJobId}/`, pollOptions);
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          throw new Error(`${data?.error || "Failed to poll generation job"} (${resp.status})`);
        }

        if (cancelled) return;

        const job = data?.job;
        const statusValRaw = job?.status || "running";
        const statusVal = String(statusValRaw || "").toLowerCase().trim() || "running";
        setGenerationStatus(statusVal);
        generationConsecutivePollErrorsRef.current = 0;

        if (statusVal === "completed") {
          if (!generationSuccessSoundPlayedRef.current) {
            generationSuccessSoundPlayedRef.current = true;
            playGenerationSuccessChime();
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              try { new Notification("Your website is ready!", { body: "Generation complete. Click to view your new site." }); } catch (_) {}
            }
          }
          setIsGenerating(false);
          stopGenerationPolling();
          const params = data?.edit_url_params || {};
          const targetUsername = params.username || username;
          const targetDomain = params.domain || createdWebsiteInfo?.domain;
          const targetSlug = params.version_slug || createdWebsiteInfo?.version_slug;
          if (targetUsername && targetDomain && targetSlug) {
            navigate(`/edit/${targetUsername}/${targetDomain}/${targetSlug}?tutorial_prompt=1`);
            return;
          }
          // Fallback: if missing params, keep user on create page with a continue link.
          setPostCreateMessage("Website generated, but we couldn't redirect automatically. Please click Continue to Editor.");
          return;
        }

        if (statusVal === "failed") {
          const msg = data?.error_message || job?.error_message || "Generation failed. Please try again later.";
          console.error("[CreateNewWebsite] website generation failed:", msg);

          // Early-failure silent retry: if the job failed shortly after start
          // and we haven't used the early-failure retry yet, restart silently.
          const elapsedSinceStart = Date.now() - startedAt;
          const isEarlyFailure = elapsedSinceStart < GENERATION_EARLY_FAILURE_WINDOW_MS;
          const retryParams = generationStartParamsRef.current;
          const canRetryEarly =
            isEarlyFailure &&
            !generationAutoRetryUsedRef.current.earlyFailure &&
            shouldAutoRetryGenerationError(new Error(msg)) &&
            retryParams?.websiteId &&
            !cancelled;

          if (canRetryEarly) {
            generationAutoRetryUsedRef.current.earlyFailure = true;
            console.log("[CreateNewWebsite] attempting silent early-failure generation retry…");
            try {
              if (GENERATION_START_AUTO_RETRY_DELAY_MS) {
                await sleep(GENERATION_START_AUTO_RETRY_DELAY_MS);
              }
              if (cancelled) return;
              const newJobId = await startWebsiteGenerationJob({
                websiteId: retryParams.websiteId,
                aiInputAssetIds: retryParams.aiInputAssetIds || [],
                aiPrompt: retryParams.aiPrompt ?? null,
                templateName: retryParams.templateName ?? null,
                generateAiImages: retryParams.generateAiImages ?? false,
                useProfileForImages: retryParams.useProfileForImages ?? false,
                silent: true,
              });
              generationStartedAtRef.current = Date.now();
              if (newJobId === generationJobId) {
                // Same job ID (backend idempotent): continue polling in this
                // closure — setGenerationJobId with the same value would be a
                // no-op and wouldn't restart the effect.
                generationPollerRef.current = setTimeout(poll, GENERATION_POLL_INTERVAL_MS);
              } else {
                // Different job ID: update state to trigger a fresh polling cycle.
                setGenerationJobId(newJobId);
              }
              return;
            } catch (retryErr) {
              console.error("[CreateNewWebsite] early-failure retry also failed:", retryErr);
              // Fall through to show the failure modal.
            }
          }

          failGeneration(msg);
          return;
        }

        // Defensive: if backend ever returns an unexpected status, treat it as a failure instead of silently stopping.
        if (statusVal !== "queued" && statusVal !== "running") {
          console.error("[CreateNewWebsite] unexpected generation status:", statusValRaw);
          failGeneration("Generation failed. Please try again later.");
          return;
        }

        generationPollerRef.current = setTimeout(poll, GENERATION_POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        // Polling can fail transiently (network hiccup, tab throttling, brief backend restart).
        // Do not give up immediately, since the backend job may still be running.
        console.error("[CreateNewWebsite] website generation poll failed:", e);
        const elapsed = Date.now() - startedAt;
        if (elapsed > GENERATION_POLL_TIMEOUT_MS) {
          failGeneration("Generation is taking longer than expected. Please try again later.");
          return;
        }
        generationConsecutivePollErrorsRef.current += 1;
        const failures = generationConsecutivePollErrorsRef.current;
        if (failures > GENERATION_MAX_CONSECUTIVE_POLL_ERRORS) {
          failGeneration("We lost connection while generating your website. It may still be running — please try again shortly.");
          return;
        }
        // Backoff a bit, then retry.
        const delay = Math.min(GENERATION_POLL_ERROR_BACKOFF_MAX_MS, GENERATION_POLL_INTERVAL_MS * (failures + 1));
        generationPollerRef.current = setTimeout(poll, delay);
      }
    };

    // Poll no more than once every 2s; start after the interval (we already have initial "queued" status locally).
    generationPollerRef.current = setTimeout(poll, GENERATION_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch (e) {
        // no-op
      }
      stopGenerationPolling();
    };
    // NOTE: showGenerationStartFailure and startWebsiteGenerationJobWithAutoRetry
    // are intentionally excluded — they are NOT used inside the poll.
    // startWebsiteGenerationJob IS used (for the early-failure retry) and is
    // stable (useCallback with []).  Previously, the unstable
    // startWebsiteGenerationJobWithAutoRetry in this deps array caused the
    // effect to tear down every render.  Combined with the 250ms UI-tick
    // re-renders for the phase animation, the 2s poll timeout was perpetually
    // cancelled before it could fire, leaving the modal stuck permanently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGenerating,
    generationJobId,
    navigate,
    username,
    createdWebsiteInfo?.domain,
    createdWebsiteInfo?.version_slug,
    failGeneration,
    startWebsiteGenerationJob,
    stopGenerationPolling,
  ]);

  // Friendly generation phase timeline (frontend-only status text).
  useEffect(() => {
    if (!isGenerating) {
      setGenerationPhaseLabel("Reading");
      return undefined;
    }

    const startedAt = generationStartedAtRef.current || Date.now();
    if (!generationStartedAtRef.current) {
      generationStartedAtRef.current = startedAt;
    }

    const computeBaseLabel = (elapsedMs) => {
      let acc = 0;
      for (const phase of GENERATION_UI_PHASES) {
        acc += phase.ms;
        if (elapsedMs < acc) return phase.label;
      }
      return GENERATION_UI_PHASES[GENERATION_UI_PHASES.length - 1]?.label || "Testing...";
    };

    const updateUi = () => {
      // Phase label updates only when it changes; tick forces a re-render so dots animate continuously.
      setGenerationPhaseLabel(computeBaseLabel(Date.now() - startedAt));
      setGenerationUiTick((t) => t + 1);
    };

    updateUi();
    const intervalId = setInterval(() => {
      updateUi();
    }, 250); // phase selection can be responsive

    return () => clearInterval(intervalId);
  }, [isGenerating]);

  const generationPhaseDots = (() => {
    if (!isGenerating) return "";
    const startedAt = generationStartedAtRef.current || Date.now();
    const idx = Math.floor((Date.now() - startedAt) / GENERATION_UI_DOT_STEP_MS) % GENERATION_UI_DOTS.length;
    return GENERATION_UI_DOTS[idx] || ".";
  })();

  const handleRetryGeneration = useCallback(async () => {
    try {
      const ids = Array.from(
        new Set(
          inputAssets
            .filter(
              (a) =>
                a.uploadStatus === "uploaded" &&
                a.serverId &&
                (a.extractionStatus || "pending") === "completed"
            )
            .map((a) => a.serverId)
        )
      );
      if (!createdWebsiteInfo?.website_id || !ids.length) return;

      const promptForJob = normalizedAiPrompt ? normalizedAiPrompt : null;
      const templateNameForJob =
        generationStartParamsRef.current?.templateName || selectedTemplate?.value || null;

      setPostCreateMessage("");
      setGenerationError("");
      setIsGenerationErrorModalOpen(false);
      setIsGenerating(true);
      setGenerationStatus("queued");
      generationSuccessSoundPlayedRef.current = false;
      generationStartedAtRef.current = Date.now();
      generationAutoRetryUsedRef.current = { start: false, earlyFailure: false };
      const retryGenerateAiImages = generationStartParamsRef.current?.generateAiImages ?? false;
      const retryUseProfileForImages = generationStartParamsRef.current?.useProfileForImages ?? false;
      generationStartParamsRef.current = {
        websiteId: createdWebsiteInfo.website_id,
        aiInputAssetIds: ids,
        aiPrompt: promptForJob,
        templateName: templateNameForJob,
        generateAiImages: retryGenerateAiImages,
        useProfileForImages: retryUseProfileForImages,
      };

      const jobId = await startWebsiteGenerationJobWithAutoRetry({
        websiteId: createdWebsiteInfo.website_id,
        aiInputAssetIds: ids,
        aiPrompt: promptForJob,
        templateName: templateNameForJob,
        generateAiImages: retryGenerateAiImages,
        useProfileForImages: retryUseProfileForImages,
      });
      setGenerationJobId(jobId);
    } catch (e) {
      showGenerationStartFailure(e);
    }
  }, [
    createdWebsiteInfo?.website_id,
    normalizedAiPrompt,
    inputAssets,
    selectedTemplate?.value,
    showGenerationStartFailure,
    startWebsiteGenerationJobWithAutoRetry,
  ]);

  const handleRetryFailedUploads = async () => {
    if (!createdWebsiteInfo?.website_id) return;
    setPostCreateMessage("");
    const failedCount = await uploadQueuedOrFailedAssets(createdWebsiteInfo.website_id);
    if (failedCount > 0) {
      setPostCreateMessage(
        `${failedCount} asset${failedCount === 1 ? "" : "s"} still failed to upload — you can retry again or continue to the editor.`
      );
      return;
    }
    setPostCreateMessage("All assets uploaded successfully. You can continue to the editor.");
  };

  // Reset on (re-)mount so React 18 StrictMode double-mount cycle doesn't leave it stale.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      Object.values(extractionPollersRef.current || {}).forEach((t) => {
        try {
          clearTimeout(t);
        } catch (e) {
          // no-op
        }
      });
      extractionPollersRef.current = {};
      stopGenerationPolling();
    };
  }, []);

  return (
    <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
      <DashboardNavBar username={username} />

      <SubscriptionPlansModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        notice={
          <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
            <div className={`${typography.h5} ${textColors.primary}`}>
              Upgrade required to create more websites
            </div>
            <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
              Your current plan allows up to{" "}
              <span className={`${textColors.primary} font-medium`}>
                {createLimitInfo?.max_websites ?? "0"}
              </span>{" "}
              website draft{Number(createLimitInfo?.max_websites || 0) === 1 ? "" : "s"}.
              {createLimitInfo?.current_websites != null ? (
                <>
                  {" "}
                  You currently have{" "}
                  <span className={`${textColors.primary} font-medium`}>
                    {createLimitInfo.current_websites}
                  </span>
                  .
                </>
              ) : null}
            </div>
          </div>
        }
      />

      {/* Friendly error modal (do not show raw backend errors) */}
      {isGenerationErrorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm px-6">
          <div className={`bg-white dark:bg-dark-900/80 backdrop-blur-xl w-full max-w-lg rounded-3xl border ${borders.glass} p-6 shadow-xl`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className={`${typography.h4} ${textColors.primary}`}>We couldn’t generate your website</div>
                <div className={`mt-2 ${typography.body} ${textColors.secondary}`}>
                  Something went wrong while generating your site. Please try again later.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsGenerationErrorModalOpen(false)}
                aria-label="Dismiss"
                className={`inline-flex items-center justify-center h-9 w-9 rounded-md border ${borders.default} ${surfaces.secondary} ${surfaces.hover} ${textColors.secondary}`}
              >
                <span className="text-lg leading-none">×</span>
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {createdWebsiteInfo &&
              inputAssets.some((a) => a.uploadStatus === "uploaded" && a.serverId && a.extractionStatus === "completed") ? (
                <button
                  type="button"
                  onClick={handleRetryGeneration}
                  className={`px-4 py-2 rounded-md ${typography.button} ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText}`}
                >
                  Retry Generation
                </button>
              ) : null}

              {createdWebsiteInfo ? (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/edit/${username}/${createdWebsiteInfo.domain}/${createdWebsiteInfo.version_slug}?tutorial_prompt=1`
                    )
                  }
                  className={`px-4 py-2 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.button} ${textColors.primary}`}
                >
                  Continue to Editor
                </button>
              ) : null}
            </div>

            <div className={`mt-4 ${typography.caption} ${textColors.tertiary}`}>
              If this keeps happening, try continuing to the editor and making small edits manually.
            </div>
          </div>
        </div>
      )}

      {isGenerating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm px-6">
          <div className={`bg-white dark:bg-dark-900/80 backdrop-blur-xl w-full max-w-lg rounded-3xl border ${borders.glass} p-6 shadow-xl`}>
            <div className="flex items-center justify-center gap-3">
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 border-current ${textColors.primary}`} />
              <div className={`${typography.h4} ${textColors.primary}`}>Generating your website…</div>
            </div>
            <div className={`mt-3 text-center ${typography.body} ${textColors.secondary}`}>
              Status: {(generationPhaseLabel || "Reading") + generationPhaseDots}
            </div>
            <div className={`mt-4 text-center ${typography.bodySmall} ${textColors.secondary}`}>
              Please keep this tab open.
              <br />
              This usually takes about 5 minutes.
            </div>
            <div className={`mt-3 text-center ${typography.bodyLarge} ${textColors.primary}`}>
              We will email you when it is done!
            </div>

            {typeof Notification !== "undefined" && notificationPermission === "default" && !notificationDismissed && (
              <div className={`mt-5 pt-4 border-t ${borders.glass}`}>
                <div className={`${typography.bodySmall} ${textColors.secondary} mb-3`}>
                  Notify me when generation is complete
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const permission = await Notification.requestPermission();
                        setNotificationPermission(permission);
                      } catch (_) {
                        setNotificationDismissed(true);
                      }
                    }}
                    className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    Allow
                  </button>
                  <button
                    onClick={() => setNotificationDismissed(true)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium ${textColors.secondary} hover:bg-black/5 dark:hover:bg-white/5 transition-colors`}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="flex-1 pt-12 sm:pt-16">
        {/* Fixed top bar (streamline) */}
        <div className="fixed top-12 sm:top-16 left-0 right-0 z-40">
          <div ref={streamlineBarRef} className={`relative bg-white/80 dark:bg-dark-900/80 border-b ${borders.glass} shadow-sm backdrop-blur-md`}>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-10 right-10 h-44 w-44 bg-blue-500/25 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 bg-purple-500/25 rounded-full blur-3xl" />
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-2">
              {/* Mobile: simplified layout with back button, title, and create button */}
              <div className="sm:hidden">
                <div className="flex items-center justify-between gap-2 py-1">
                  <button
                    onClick={() => navigate(`/dashboard/${username}`)}
                    aria-label="Back to dashboard"
                    className={`inline-flex items-center justify-center h-9 w-9 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} ${textColors.secondary} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    <ArrowLeftIcon size="sm" />
                  </button>
                  <h1 className={`flex-1 ${typography.h4} ${textColors.primary} text-center`}>
                    Create Website
                  </h1>
                  <button
                    onClick={handleCreateWebsite}
                    disabled={createButtonDisabled}
                    className={`relative inline-flex items-center justify-center whitespace-nowrap px-4 py-2 rounded-full ${typography.button} transition-all ${
                      createButtonDisabled
                        ? "bg-gray-300 dark:bg-dark-700 text-gray-700 dark:text-dark-300 cursor-not-allowed"
                        : `bg-green-400 hover:bg-green-500 text-white focus:ring-4 focus:ring-green-200 shadow ${
                            isReadyToCreate ? "ring-1 ring-green-400/20 shadow-[0_0_7px_rgba(74,222,128,0.15)]" : ""
                          }`
                    }`}
                  >
                    {isReadyToCreate && !createButtonDisabled ? (
                      <span
                        aria-hidden="true"
                        className="absolute inset-[-1px] rounded-full bg-gradient-to-r from-emerald-500/15 via-green-400/10 to-emerald-500/15 create-cta-glow"
                        style={{ zIndex: -1 }}
                      />
                    ) : null}
                    {createButtonContent}
                  </button>
                </div>
                {/* AI coach line - mobile */}
                <div className="mt-1.5 pb-1">
                  <div className={`flex items-center justify-center gap-1.5 text-center ${typography.bodySmall} ${textColors.secondary} dark:text-white`}>
                    <span
                      className={`relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-white ${typography.caption} !text-white overflow-hidden shrink-0`}
                    >
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 ai-badge-gradient"
                      />
                      <span className="relative text-white text-xs font-bold">AI</span>
                    </span>
                    <span className="min-w-0 font-semibold text-[13px] leading-tight">
                      {aiCoachText}
                    </span>
                    <svg
                      aria-hidden="true"
                      className="relative w-3.5 h-3.5 shrink-0"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient id={sparklesGradientId} x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#a855f7" />
                          <stop offset="50%" stopColor="#ec4899" />
                          <stop offset="100%" stopColor="#3b82f6" />
                          <animateTransform
                            attributeName="gradientTransform"
                            type="translate"
                            values="-1 0; 1 0; -1 0"
                            dur="2.6s"
                            repeatCount="indefinite"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        fill={`url(#${sparklesGradientId})`}
                        d="M6 2a1 1 0 0 1 .95.68l.78 2.35 2.35.78a1 1 0 0 1 0 1.9l-2.35.78-.78 2.35a1 1 0 0 1-1.9 0l-.78-2.35-2.35-.78a1 1 0 0 1 0-1.9l2.35-.78.78-2.35A1 1 0 0 1 6 2Zm9 6a1 1 0 0 1 .95.68l.39 1.17 1.17.39a1 1 0 0 1 0 1.9l-1.17.39-.39 1.17a1 1 0 0 1-1.9 0l-.39-1.17-1.17-.39a1 1 0 0 1 0-1.9l1.17-.39.39-1.17A1 1 0 0 1 15 8Z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Desktop: full layout with all elements */}
              <div className="hidden sm:grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                {showTopBarSteps ? (
                  <div className="min-w-0 flex flex-wrap items-center justify-start gap-2 flex-nowrap overflow-x-auto pb-1">
                    {readinessItems.map((item, idx) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleTopBarStepClick(item.key)}
                        aria-label={`Go to ${item.label}`}
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} transition-colors`}
                      >
                        <span
                          className={`relative h-5 w-5 rounded-full border flex items-center justify-center transition-all duration-200 ${
                            item.done
                              ? "bg-transparent border-green-400"
                              : "bg-transparent border-gray-300 dark:border-dark-500"
                          }`}
                          aria-hidden="true"
                        >
                          {item.done ? (
                            <svg
                              className="w-3 h-3 text-green-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.704 5.29a1 1 0 010 1.414l-7.2 7.2a1 1 0 01-1.414 0l-3.6-3.6a1 1 0 011.414-1.414l2.893 2.893 6.493-6.493a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <span className={`${typography.caption} ${textColors.secondary} font-semibold leading-none`}>
                              {idx + 1}
                            </span>
                          )}
                        </span>
                        <span className={`${typography.caption} ${textColors.secondary}`}>
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div />
                )}

                <h1 className={`${typography.h4} ${textColors.primary} text-center`}>
                  Create Website
                </h1>

                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={handleCreateWebsite}
                    disabled={createButtonDisabled}
                    className={`relative inline-flex items-center justify-center whitespace-nowrap px-4 py-2 rounded-full ${typography.button} transition-all ${
                      createButtonDisabled
                        ? "bg-gray-300 dark:bg-dark-700 text-gray-700 dark:text-dark-300 cursor-not-allowed"
                        : `bg-green-400 hover:bg-green-500 text-white focus:ring-4 focus:ring-green-200 shadow ${
                            isReadyToCreate ? "ring-1 ring-green-400/20 shadow-[0_0_7px_rgba(74,222,128,0.15)]" : ""
                          }`
                    }`}
                  >
                    {isReadyToCreate && !createButtonDisabled ? (
                      <span
                        aria-hidden="true"
                        className="absolute inset-[-1px] rounded-full bg-gradient-to-r from-emerald-500/15 via-green-400/10 to-emerald-500/15 create-cta-glow"
                        style={{ zIndex: -1 }}
                      />
                    ) : null}
                    {createButtonContent}
                  </button>
                  <button
                    onClick={() => navigate(`/dashboard/${username}`)}
                    className={`inline-flex items-center justify-center whitespace-nowrap px-3 py-2 border ${borders.glass} rounded-full ${typography.button} ${textColors.secondary} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    Cancel
                  </button>
                  <div
                    className={`whitespace-nowrap px-3 py-1 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${typography.caption} ${textColors.secondary}`}
                  >
                    {requiredStepsComplete}/{requiredStepsTotal}
                  </div>
                </div>
              </div>

              {/* AI coach line - desktop */}
              <div className="hidden sm:block mt-1.5">
                <div className={`flex items-center justify-center gap-2 text-center ${typography.bodySmall} ${textColors.secondary} dark:text-white`}>
                  <span
                    className={`relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white ${typography.caption} !text-white overflow-hidden`}
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 ai-badge-gradient"
                    />
                    <span className="relative text-white font-bold">AI</span>
                  </span>
                  <span className="min-w-0 font-semibold text-[15px]">
                    {aiCoachText}
                  </span>
                  <svg
                    aria-hidden="true"
                    className="relative w-4 h-4 shrink-0"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id={sparklesGradientId} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#a855f7" />
                        <stop offset="50%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#3b82f6" />
                        <animateTransform
                          attributeName="gradientTransform"
                          type="translate"
                          values="-1 0; 1 0; -1 0"
                          dur="2.6s"
                          repeatCount="indefinite"
                        />
                      </linearGradient>
                    </defs>
                    <path
                      fill={`url(#${sparklesGradientId})`}
                      d="M6 2a1 1 0 0 1 .95.68l.78 2.35 2.35.78a1 1 0 0 1 0 1.9l-2.35.78-.78 2.35a1 1 0 0 1-1.9 0l-.78-2.35-2.35-.78a1 1 0 0 1 0-1.9l2.35-.78.78-2.35A1 1 0 0 1 6 2Zm9 6a1 1 0 0 1 .95.68l.39 1.17 1.17.39a1 1 0 0 1 0 1.9l-1.17.39-.39 1.17a1 1 0 0 1-1.9 0l-.39-1.17-1.17-.39a1 1 0 0 1 0-1.9l1.17-.39.39-1.17A1 1 0 0 1 15 8Z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isStreamline ? "pt-28 sm:pt-24" : ""}`}>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* Preview Card */}
            <div
              ref={styleTemplateCardRef}
              className={`${previewCardClassName} ${previewCardLayout}`}
              onMouseEnter={() => isStreamline && setHoverHintArea("preview")}
              onMouseLeave={() => isStreamline && setHoverHintArea(null)}
            >
              <div className="flex flex-wrap items-start gap-3">
                  {isStreamline ? (
                    <div className="flex flex-col items-center gap-1.5 shrink-0">
                      <div
                        className={`relative h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden ${stepGlowClass(
                          "style_template"
                        )}`}
                      >
                        <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500" />
                        <span
                          aria-hidden="true"
                          className={`absolute inset-0 bg-green-400 transition-opacity duration-1000 ease-out ${
                            isReadyToCreate ? "opacity-100" : "opacity-0"
                          }`}
                        />
                        <span className="relative">4</span>
                      </div>
                      <span
                        className={`sm:hidden px-2 py-0.5 rounded-full border ${borders.default} ${surfaces.secondary} ${typography.caption} ${textColors.secondary} text-[10px] whitespace-nowrap`}
                      >
                        {styleTemplateComplete ? "Ready" : "Required"}
                      </span>
                    </div>
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h2 className={`${typography.h4} ${textColors.primary}`}>{previewTitle}</h2>
                      {isStreamline && (
                      <span
                        className={`hidden sm:inline-flex px-2 py-1 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${typography.caption} ${textColors.secondary} shrink-0`}
                      >
                          {styleTemplateComplete ? "Ready" : "Required"}
                        </span>
                      )}
                    </div>
                    {showPreviewDescription && (
                      <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                        A quick look at your homepage with the selected style and template.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 w-full sm:w-auto">
                    {previewPages.length > 1 && (
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${textColors.secondary}`}>Page:</span>
                          <select
                            value={previewSubdomain ?? ""}
                            onChange={(e) => setPreviewSubdomain(e.target.value)}
                            className={`px-2 py-1 text-sm border rounded bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${borders.glass} ${textColors.primary}`}
                          >
                          {previewPages.map((page) => {
                            const label = page?.name || (page?.subdomain ? page.subdomain : "Home");
                            return (
                              <option key={page.id} value={page.subdomain || ""}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                    {isPreviewPagesLoading && (
                      <span className={`text-sm ${textColors.secondary}`}>Loading pages...</span>
                    )}
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${textColors.secondary} hidden md:inline`}>Device:</span>
                      <div className={`inline-flex rounded-md border ${borders.default} overflow-hidden`}>
                        <button
                          type="button"
                          onClick={() => setPreviewMode("responsive")}
                          aria-label="Responsive"
                          className={`px-3 py-1 text-sm flex items-center ${
                            previewMode === "responsive"
                              ? `${surfaces.tertiary} ${textColors.primary}`
                              : `${surfaces.primary} ${textColors.secondary}`
                          }`}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23,2V12a1,1,0,0,1-2,0V4.414L4.414,21H12a1,1,0,0,1,0,2H2a1,1,0,0,1-1-1V12a1,1,0,0,1,2,0v7.586L19.586,3H12a1,1,0,0,1,0-2H22A1,1,0,0,1,23,2Z"></path>
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewMode("phone")}
                          aria-label="Phone"
                          className={`px-3 py-1 text-sm flex items-center ${
                            previewMode === "phone"
                              ? `${surfaces.tertiary} ${textColors.primary}`
                              : `${surfaces.primary} ${textColors.secondary}`
                          }`}
                        >
                          <MobileOutlined style={{ fontSize: 14 }} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewMode("desktop")}
                          aria-label="Desktop"
                          className={`px-3 py-1 text-sm flex items-center ${
                            previewMode === "desktop"
                              ? `${surfaces.tertiary} ${textColors.primary}`
                              : `${surfaces.primary} ${textColors.secondary}`
                          }`}
                        >
                          <DesktopOutlined style={{ fontSize: 14 }} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {isLoadingStyleTemplateOptions ? (
                    <div className={`w-full min-h-[520px] flex items-center justify-center text-center ${typography.body} ${textColors.secondary}`}>
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400 mr-2" />
                        <span>Loading styles and templates...</span>
                      </div>
                    </div>
                  ) : styleTemplateOptionsError ? (
                    <div className="min-h-[520px] flex items-center justify-center">
                      <NetworkErrorState
                        title="Network error :("
                        description="We couldn't load styles and templates right now. Please check your connection and try again."
                        onRetry={loadStyleTemplateOptions}
                        className="py-10"
                      />
                    </div>
                  ) : (
                    <>
                      {isStreamline && (
                        <div className="space-y-3">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <div
                              className={`flex items-center gap-1 ${typography.label} ${textColors.secondary} w-auto sm:w-24 shrink-0 sm:whitespace-nowrap`}
                            >
                              <span>Style</span>
                              {stylePagination.isExpanded ? (
                                <span className={`${typography.caption} ${textColors.tertiary}`}>(All)</span>
                              ) : stylePagination.totalPages > 1 ? (
                                <span className={`${typography.caption} ${textColors.tertiary}`}>
                                  ({stylePagination.currentPage + 1}/{stylePagination.totalPages})
                                </span>
                              ) : null}
                            </div>
                            <div className="flex-1 min-w-0">
                              <StyleSelection
                                styles={availableStyles}
                                selectedStyle={selectedStyle}
                                onStyleSelect={handleStyleSelect}
                                onPaginationChange={handleStylePaginationChange}
                                itemsPerPage={styleItemsPerPage}
                                variant="compact"
                                useColorAccents={false}
                              />
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <div className={`${typography.label} ${textColors.secondary} w-auto sm:w-24 shrink-0 sm:whitespace-nowrap`}>Template</div>
                            <div className="flex-1 min-w-0">
                              <TemplateSelection
                                templates={availableTemplates}
                                selectedTemplate={selectedTemplate}
                                onTemplateSelect={handleTemplateSelect}
                                itemsPerPage={styleItemsPerPage}
                                variant="compact"
                                useColorAccents={false}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Reserve height for preview area to avoid layout collapse / scroll jumps */}
                      <div className={isStreamline ? "min-h-[520px]" : ""}>
                        {isPreviewBusy && (
                          <div className={`w-full h-full min-h-[520px] flex items-center justify-center text-center ${typography.body} ${textColors.muted}`}>
                            Loading preview...
                          </div>
                        )}
                        {previewError && !isPreviewBusy && (
                          <NetworkErrorState
                            title={
                              typeof previewError === "string" &&
                              previewError.toLowerCase().includes("network error")
                                ? "Network error :("
                                : "Couldn't load the preview"
                            }
                            description={
                              typeof previewError === "string" &&
                              previewError.toLowerCase().includes("network error")
                                ? "We couldn't load the preview right now. Please check your connection and try again."
                                : previewError
                            }
                            onRetry={() => setPreviewRetryNonce((n) => n + 1)}
                            className="py-10"
                          />
                        )}
                        {!isPreviewBusy && !previewError && previewReady && (
                          <div
                            className={`relative rounded-xl border ${borders.default} overflow-hidden bg-slate-100 dark:bg-dark-950`}
                            onMouseEnter={useScreenshotPreview ? () => setIsLivePreviewRevealed(true) : undefined}
                            onTouchStart={useScreenshotPreview ? () => setIsLivePreviewRevealed(true) : undefined}
                          >
                            <div
                              className={`${useScreenshotPreview ? "absolute inset-0 overflow-hidden" : ""} ${isStreamline ? "origin-top scale-90 transform" : ""}`.trim()}
                            >
                              <PreviewDeviceStage
                                mode={previewMode}
                                contextOverrides={{ onNavigate: handlePreviewNavigate }}
                              >
                                {({ containerWidth }) => (
                                  <PreviewWidthSync width={containerWidth}>
                                    <div data-testid="template-preview-website" className="w-full">
                                      <PreviewWebsiteRouter
                                        username={previewInfo.username}
                                        domain={previewInfo.domain}
                                        subdomain={previewSubdomain}
                                        versionSlug={previewInfo.version_slug}
                                        onReady={handlePreviewWebsiteReady}
                                      />
                                    </div>
                                  </PreviewWidthSync>
                                )}
                              </PreviewDeviceStage>
                            </div>
                            {useScreenshotPreview && (
                              <>
                                {/* The screenshot is in normal flow so it defines the preview height; the live
                                    website above is absolutely positioned and clipped to this (smaller) height,
                                    so it never bleeds out below the screenshot. */}
                                <div
                                  className={`relative transition-opacity duration-500 ease-out ${
                                    isLivePreviewRevealed ? "opacity-0 pointer-events-none" : "opacity-100"
                                  }`}
                                  data-testid="template-preview-screenshot-overlay"
                                >
                                  <img
                                    src={previewScreenshotSrc}
                                    alt={`Preview of ${previewInfo.domain}`}
                                    onError={() => setPreviewScreenshotFailed(true)}
                                    className="block w-full h-auto"
                                    loading="eager"
                                    draggable={false}
                                    data-testid="template-preview-screenshot"
                                  />
                                </div>
                                <div
                                  className={`pointer-events-none absolute bottom-3 right-3 z-10 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-white shadow-lg backdrop-blur-sm transition-opacity duration-300 ${
                                    isLivePreviewRevealed ? "opacity-0" : "opacity-100"
                                  }`}
                                  data-testid="template-preview-live-hint"
                                >
                                  {previewWebsiteReady ? (
                                    <span className="text-xs font-medium">Hover to view live preview</span>
                                  ) : (
                                    <>
                                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                                      <span className="text-xs font-medium">Preparing live preview…</span>
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {!isPreviewBusy && !previewError && !previewReady && (
                          <div className={`w-full h-full min-h-[520px] flex items-center justify-center text-center ${typography.body} ${textColors.muted}`}>
                            <div className="flex flex-col items-center gap-3">
                              <div
                                className={`h-12 w-12 rounded-2xl border ${borders.default} ${surfaces.primary} flex items-center justify-center shadow-sm`}
                                aria-hidden="true"
                              >
                                {/* Artistic/palette-ish icon */}
                                <svg
                                  className="w-6 h-6"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 3C6.477 3 2 7.037 2 12.01 2 16.97 6.02 21 10.98 21h.77c1.57 0 2.67-1.54 2.18-3.02l-.3-.9c-.2-.6.25-1.23.9-1.23h2.16c2.9 0 5.31-2.35 5.31-5.25C24 6.92 18.97 3 12 3Z"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinejoin="round"
                                  />
                                  <path d="M7.6 12.1a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z" fill="currentColor" opacity="0.75" />
                                  <path d="M10.7 8.4a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z" fill="currentColor" opacity="0.7" />
                                  <path d="M15 8.7a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z" fill="currentColor" opacity="0.65" />
                                  <path d="M17.1 12.3a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2Z" fill="currentColor" opacity="0.6" />
                                </svg>
                              </div>
                              <div>Select a style to see a preview.</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {isStreamline && postCreatePanel}
              </div>
            {/* Step 1: Website Details */}
            <div
              ref={detailsCardRef}
              className={`${surfaces.glassCard} rounded-3xl border ${borders.glass} p-6 shadow-sm ${detailsCardLayout}`}
              onMouseEnter={() => isStreamline && setHoverHintArea("details")}
              onMouseLeave={() => isStreamline && setHoverHintArea(null)}
            >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1.5 shrink-0">
                    <div
                      className={`relative h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden ${stepGlowClass(
                        "details"
                      )}`}
                    >
                      <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500" />
                      <span
                        aria-hidden="true"
                        className={`absolute inset-0 bg-green-400 transition-opacity duration-1000 ease-out ${
                          isReadyToCreate ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <span className="relative">1</span>
                    </div>
                    <span
                      className={`sm:hidden px-2 py-0.5 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${typography.caption} ${textColors.secondary} text-[10px] whitespace-nowrap`}
                    >
                      {detailsComplete ? "Ready" : "Required"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h2 className={`${typography.h4} ${textColors.primary}`}>Website identity</h2>
                      <span
                        className={`hidden sm:inline-flex px-2 py-1 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${typography.caption} ${textColors.secondary} shrink-0`}
                      >
                        {detailsComplete ? "Ready" : "Required"}
                      </span>
                    </div>
                    <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                      Your name and domain anchor the AI-generated site.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label htmlFor="websiteName" className={`${typography.label} mb-2 block ${textColors.bright}`}>
                      Website Name <span className="text-red-600 dark:text-red-400">*</span>
                    </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="websiteName"
                      value={websiteName}
                      onChange={(e) => setWebsiteName(e.target.value)}
                      placeholder="e.g., My Portfolio"
                      className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        websiteName.trim() && !nameValidation.isValid 
                          ? "border-red-300 dark:border-red-700" 
                          : websiteName.trim() && nameValidation.isValid 
                          ? "border-green-300 dark:border-green-700" 
                          : borders.glass
                      }`}
                    />
                    {websiteName.trim() && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {nameValidation.isValid ? (
                          <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  {!nameValidation.isValid && nameValidation.message && (
                    <p className={`mt-1 ${typography.bodySmall} text-red-600 dark:text-red-400`}>
                      {nameValidation.message}
                    </p>
                  )}
                </div>
                  <div>
                    <label htmlFor="domain" className={`${typography.label} mb-2 block ${textColors.bright}`}>
                      {domainLabel}
                    </label>
                    <div className="flex flex-col gap-2">
                      <div
                        className={`sm:hidden ${typography.bodySmall} ${textColors.secondary} font-mono`}
                        title={`${APP_DOMAIN}/${username}/`}
                      >
                        <div className="min-w-0 truncate">{APP_DOMAIN}/{username}/</div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div
                          className={`w-full flex rounded-md shadow-sm overflow-hidden border ${
                            domain.trim() && !domainValidation.isValid
                              ? "border-red-300 dark:border-red-700"
                              : domain.trim() && domainValidation.isValid
                              ? "border-green-300 dark:border-green-700"
                              : borders.glass
                          }`}
                        >
                          <div
                            className={`hidden sm:block px-3 py-2 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.secondary} ${typography.bodySmall} font-mono whitespace-nowrap`}
                          >
                            {APP_DOMAIN}/{username}/
                          </div>
                          <div className="relative flex-1 min-w-0">
                            <input
                              type="text"
                              id="domain"
                              value={domain}
                              onChange={handleDomainChange}
                              onFocus={handleDomainFocus}
                              placeholder="my-portfolio"
                              className={`w-full px-3 py-2 pr-10 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                            />
                            {domain.trim() && (
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                {domainValidation.isValid ? (
                                  <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleGenerateDomain}
                          disabled={!websiteName.trim()}
                          aria-label="Regenerate domain"
                          title="Regenerate domain"
                          className={`h-9 w-9 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.secondary} ${surfaces.hover} flex items-center justify-center ${
                            !websiteName.trim() ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 4v6h6M20 20v-6h-6M5 19a9 9 0 0 0 14-7M19 5a9 9 0 0 0-14 7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {!domainValidation.isValid && domainValidation.message && (
                      <p className={`mt-1 ${typography.bodySmall} text-red-600 dark:text-red-400`}>
                        {domainValidation.message}
                      </p>
                    )}
                </div>
              </div>
            </div>

            {/* Step 4: Upload Assets (optional) */}
            <div
              ref={assetsCardRef}
              className={`${surfaces.glassCard} rounded-3xl border ${borders.glass} p-6 shadow-sm ${assetsCardLayout}`}
              onMouseEnter={() => isStreamline && setHoverHintArea("assets")}
              onMouseLeave={() => isStreamline && setHoverHintArea(null)}
            >
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div
                    className={`relative h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden ${stepGlowClass(
                      "assets"
                    )}`}
                  >
                    <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500" />
                    <span
                      aria-hidden="true"
                      className={`absolute inset-0 bg-green-400 transition-opacity duration-1000 ease-out ${
                        isReadyToCreate ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="relative">{isStreamline ? 2 : 4}</span>
                  </div>
                  <span
                    className={`sm:hidden px-2 py-0.5 rounded-full border ${borders.default} ${surfaces.secondary} ${typography.caption} ${textColors.secondary} text-[10px] whitespace-nowrap`}
                  >
                    Optional
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className={`${typography.h4} ${textColors.primary}`}>
                      {isStreamline ? "Files, images & info (used to build your site)" : "AI assets (optional)"}
                    </h2>
                      <span
                        className={`hidden sm:inline-flex px-2 py-1 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${typography.caption} ${textColors.secondary} shrink-0`}
                      >
                      Optional
                    </span>
                  </div>
                  <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                    Upload your resume, projects, and pictures you want the AI to use.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <div
                  className={`grid grid-cols-1 gap-4 ${
                    showLibraryPicker && !useExpandedLibraryPickerLayout ? "lg:grid-cols-2" : ""
                  }`}
                >
                {/* Left: upload new files */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  className={`w-full border-2 border-dashed ${borders.glass} rounded-2xl p-4 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${
                    useExpandedLibraryPickerLayout ? "order-2" : "order-1"
                  }`}
                >
                  <div className="flex flex-col items-center justify-center text-center">
                    <CloudUploadOutlined style={{ fontSize: 28 }} className={`${textColors.secondary}`} />
                    <input
                      ref={filePickerRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.csv,.xls,.xlsx,.ppt,.pptx"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={handlePickFiles}
                      className={`mt-2 px-4 py-2 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.button} ${textColors.primary}`}
                    >
                      Upload new files
                    </button>
                  </div>
                  <div className={`${typography.caption} ${textColors.secondary} mt-3 text-center`}>
                    Drag & drop here. Max 25MB
                  </div>
                  <div className={`${typography.caption} ${textColors.secondary} mt-1 text-center`}>
                    Supported files: PDF, DOC/DOCX, TXT, and images.
                  </div>
                </div>

                {/* Right: choose from uploaded files */}
                {showLibraryPicker ? (
                  <div
                    className={`w-full border ${borders.glass} rounded-2xl p-4 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${
                      useExpandedLibraryPickerLayout ? "order-1" : "order-2"
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className={`${typography.button} ${textColors.primary} flex-1 text-left`}>
                        Choose from uploaded files
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsLibraryPickerExpanded((prev) => !prev)}
                        aria-label={
                          useExpandedLibraryPickerLayout
                            ? "Collapse uploaded files panel"
                            : "Expand uploaded files panel"
                        }
                        title={useExpandedLibraryPickerLayout ? "Collapse" : "Expand"}
                        className={`h-8 w-8 shrink-0 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${textColors.secondary} inline-flex items-center justify-center`}
                      >
                        {useExpandedLibraryPickerLayout ? (
                          <DiagonalExpandIcon inward className="w-4 h-4" />
                        ) : (
                          <DiagonalExpandIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    {useExpandedLibraryPickerLayout && !existingAssetsError ? (
                      <div className="mb-2">
                        <div
                          className={`w-full flex items-center rounded-xl border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm overflow-hidden`}
                        >
                          <input
                            type="text"
                            value={librarySearchInput}
                            onChange={(e) => setLibrarySearchInput(e.target.value)}
                            onKeyDown={handleLibrarySearchKeyDown}
                            placeholder="Search uploaded files"
                            className={`w-full px-3 py-2 border-0 bg-transparent ${textColors.primary} ${typography.bodySmall} focus:outline-none`}
                          />
                          <button
                            type="button"
                            onClick={handleApplyLibrarySearch}
                            aria-label="Search uploaded files"
                            title="Search"
                            className={`h-9 w-9 shrink-0 inline-flex items-center justify-center border-l ${borders.default} ${textColors.secondary} ${surfaces.hover}`}
                          >
                            <SearchOutlined style={{ fontSize: 15 }} />
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {existingAssetsError ? (
                      <div className="mt-2 flex flex-col items-center gap-2 text-center">
                        <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                          {existingAssetsError}
                        </div>
                        <button
                          type="button"
                          onClick={loadExistingAssets}
                          disabled={isLoadingExistingAssets}
                          className={`px-4 py-2 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.button} ${textColors.primary} ${
                            isLoadingExistingAssets ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          Retry
                        </button>
                      </div>
                    ) : null}
                    {isLoadingExistingAssets ? (
                      <div className={`mt-2 ${typography.bodySmall} ${textColors.secondary}`}>Loading your files...</div>
                    ) : !existingAssetsError ? (
                      <div
                        className={`mt-2 ${libraryPickerMaxHeightClass} overflow-y-auto border ${borders.glass} rounded-xl bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm divide-y divide-gray-200 dark:divide-dark-700 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-dark-400 [&::-webkit-scrollbar-track]:bg-gray-100 dark:[&::-webkit-scrollbar-track]:bg-dark-700`}
                      >
                        {filteredCompletedExistingAssets.map((a) => {
                          const filename = a.original_filename || `Asset #${a.id}`;
                          const isImage = isImageFilename(filename);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => addExistingAssetToSelection(a)}
                              className={`w-full text-left px-4 py-3 ${surfaces.hover} transition-colors`}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                                    isImage
                                      ? "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300"
                                      : "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                                  }`}
                                  aria-hidden="true"
                                >
                                  {isImage ? (
                                    <LandscapeImageIcon className="w-5 h-5" />
                                  ) : (
                                    <FileOutlined style={{ fontSize: 18 }} />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className={`${typography.bodySmall} ${textColors.primary} font-medium truncate`}>
                                    {filename}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        {!filteredCompletedExistingAssets.length && !existingAssetsError ? (
                          <div className={`px-4 py-4 ${typography.bodySmall} ${textColors.secondary}`}>
                            {!availableCompletedExistingAssets.length
                              ? "No completed uploaded files yet."
                              : hasLibrarySearchQuery
                              ? "No files match your search."
                              : "No completed uploaded files yet."}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {/* "No resume?" questionnaire */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowQuestionnaire((prev) => !prev)}
                  className={`inline-flex items-center gap-1.5 ${typography.bodySmall} font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors`}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showQuestionnaire ? "rotate-90" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  No resume? Tell us about yourself
                </button>

                {showQuestionnaire && (
                  <div className={`mt-3 rounded-2xl border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm overflow-hidden divide-y ${borders.glass}`}>
                    {CV_QUESTIONNAIRE_QUESTIONS.map((q) => {
                      const isOpen = expandedQuestions.has(q.key);
                      const answer = (questionnaireAnswers[q.key] || "").trim();
                      const isAnswered = answer.length > 0;

                      return (
                        <div key={q.key}>
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedQuestions((prev) =>
                                prev.has(q.key) ? new Set() : new Set([q.key])
                              )
                            }
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left ${surfaces.hover} transition-colors`}
                          >
                            {isAnswered && !isOpen ? (
                              <svg
                                className="w-5 h-5 shrink-0 text-green-500 dark:text-green-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.704 5.29a1 1 0 010 1.414l-7.2 7.2a1 1 0 01-1.414 0l-3.6-3.6a1 1 0 011.414-1.414l2.893 2.893 6.493-6.493a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className={`w-5 h-5 shrink-0 ${textColors.secondary} transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                            <span className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                              {q.label}
                            </span>
                          </button>

                          {isOpen && (
                            <div className="px-4 pb-4 pt-1">
                              {q.hint && (
                                <div className={`${typography.caption} ${textColors.tertiary} mb-2`}>
                                  {q.hint}
                                </div>
                              )}
                              <textarea
                                rows={3}
                                value={questionnaireAnswers[q.key] || ""}
                                onChange={(e) =>
                                  setQuestionnaireAnswers((prev) => ({
                                    ...prev,
                                    [q.key]: e.target.value,
                                  }))
                                }
                                placeholder="Type your answer here..."
                                className={`w-full px-3 py-2 border rounded-xl shadow-sm resize-none bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.glass}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* AI image generation toggle */}
              <div className={`mt-4 flex items-start gap-3 p-3 rounded-xl border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm`}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={hasUploadedImage && useProfilePictureForProfessionalImages}
                  disabled={!hasUploadedImage}
                  onClick={() => setUseProfilePictureForProfessionalImages((v) => !v)}
                  className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 ${
                    !hasUploadedImage
                      ? "bg-gray-200 dark:bg-dark-700 cursor-not-allowed opacity-50"
                      : useProfilePictureForProfessionalImages
                        ? "bg-blue-600 dark:bg-blue-500 cursor-pointer"
                        : "bg-gray-300 dark:bg-dark-600 cursor-pointer"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      hasUploadedImage && useProfilePictureForProfessionalImages ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <span className={`${typography.body} ${textColors.primary} font-medium`}>
                    Use my profile picture to create professional pictures for my website
                  </span>
                  <p className={`mt-0.5 ${typography.caption} ${textColors.tertiary}`}>
                    {hasUploadedImage
                      ? "By turning this option on, you agree that new images will be generated by AI based on your profile picture."
                      : "Upload a profile picture to enable this."}
                  </p>
                </div>
              </div>

              {/* Added assets (selected from library + newly uploaded) */}
              {inputAssets.length > 0 && (
                <div className="mt-4 space-y-3">
                  {inputAssets.map((a) => (
                    <div key={a.localId} className={`p-3 rounded-xl border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm`}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className={`${typography.body} ${textColors.primary} font-medium truncate`}>
                            <div className="flex items-start justify-between gap-3 w-full">
                              <div className="min-w-0 flex-1 truncate">
                                {a.file?.name || a.originalFilename || "Unnamed file"}
                                {a.file?.size ? (
                                  <span className={`ml-2 ${typography.bodySmall} ${textColors.secondary}`}>
                                    ({formatBytes(a.file?.size)})
                                  </span>
                                ) : null}
                              </div>
                              {!a.isEditingComment ? (
                                <div className="flex flex-row items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => startEditLocalComment(a.localId)}
                                    aria-label="Edit asset"
                                    title="Edit"
                                    className={`order-1 h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${surfaces.hover} active:bg-gray-100 dark:active:bg-dark-700 ${
                                      a.isEditingComment
                                        ? "opacity-50 cursor-not-allowed"
                                        : `${textColors.secondary} hover:text-gray-900 dark:hover:text-dark-200`
                                    }`}
                                  >
                                    <EditOutlined style={{ fontSize: 18 }} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAsset(a.localId)}
                                    aria-label="Remove"
                                    title="Remove"
                                    className={`order-2 h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${surfaces.hover} active:bg-gray-100 dark:active:bg-dark-700 ${textColors.secondary} hover:text-red-600 dark:hover:text-red-400`}
                                  >
                                    <CloseOutlined style={{ fontSize: 18 }} />
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                            Type: {a.assetType} ·{" "}
                            {(() => {
                              const status = getAssetStatusPresentation(a);
                              return (
                                <span className="inline-flex items-center gap-1">
                                  <span className={textColors.secondary}>Status:</span>
                                  <span className={status.className}>{status.label}</span>
                                  {status.showCheck ? (
                                    <svg
                                      aria-hidden="true"
                                      className="w-4 h-4 text-green-600 dark:text-green-400"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.704 5.29a1 1 0 010 1.414l-7.2 7.2a1 1 0 01-1.414 0l-3.6-3.6a1 1 0 011.414-1.414l2.893 2.893 6.493-6.493a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : null}
                                </span>
                              );
                            })()}
                            {a.uploadError ? (
                              <span className="ml-2 text-red-600 dark:text-red-400">— {a.uploadError}</span>
                            ) : null}
                            {/* Intentionally do not show extraction failure reasons */}
                          </div>

                          {/* Only show extracted text when user clicks Edit */}
                          {a.isEditingComment && a.serverId && a.extractionStatus === "completed" ? (
                            <div className="mt-2">
                              <div className={`${typography.label} ${textColors.secondary} mb-1`}>AI extracted text (editable)</div>
                              <textarea
                                rows={6}
                                value={a.extractedTextUserDraft ?? ""}
                                onChange={(e) =>
                                  setInputAssets((prev) =>
                                    prev.map((x) =>
                                      x.localId === a.localId ? { ...x, extractedTextUserDraft: e.target.value } : x
                                    )
                                  )
                                }
                                placeholder="This is pre-filled from the AI extraction. You can edit it to improve results."
                                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.glass}`}
                              />
                              <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>
                                This overrides the extracted text for this file.
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          {a.uploadStatus === "failed" && (
                            <button
                              type="button"
                              onClick={() => handleRetryUpload(a.localId)}
                              className={`px-3 py-1 rounded-md ${typography.button} ${interactive.secondaryBg} ${interactive.secondaryHover} ${interactive.secondaryText}`}
                            >
                              Retry upload
                            </button>
                          )}
                          {a.serverId && a.extractionStatus === "failed" && (
                            <button
                              type="button"
                              onClick={() => handleRetryExtraction(a.serverId)}
                              className={`px-2 py-0.5 rounded ${typography.caption} ${interactive.secondaryBg} ${interactive.secondaryHover} ${interactive.secondaryText}`}
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      </div>

                      {(a.uploadStatus === "uploading" ||
                        (a.uploadStatus === "uploaded" &&
                          a.extractionStatus !== "completed" &&
                          a.extractionStatus !== "failed")) && (
                        <div className="mt-2">
                          <div className="h-2 w-full bg-gray-200 rounded">
                            <div
                              className="h-2 bg-blue-500 rounded"
                              style={{ width: `${Math.max(0, Math.min(100, a.progress || 0))}%` }}
                            />
                          </div>
                          <div className={`${typography.caption} ${textColors.secondary} mt-1`}>{a.progress || 0}%</div>
                        </div>
                      )}

                      {((a.lastSavedComment || a.comment || "").trim() ||
                        a.isEditingComment ||
                        a.commentSaveError) ? (
                      <div className="mt-3">
                        {/* Comment inline + edit overall asset (comment + extracted text) */}
                        {(a.lastSavedComment || a.comment || "").trim() ? (
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex items-center gap-2">
                              <div className={`${typography.label} ${textColors.secondary}`}>Comment:</div>
                              <div className={`${typography.bodySmall} ${textColors.secondary} truncate`}>
                                {a.lastSavedComment || a.comment}
                              </div>
                            </div>
                            {/* Edit button moved to top-right next to X */}
                          </div>
                        ) : null}

                        {a.isEditingComment ? (
                          <div className="mt-2 space-y-2">
                            {/* Comment editor (keep font size consistent with view mode) */}
                            <div className="flex items-start gap-2">
                              <input
                                type="text"
                                value={a.comment || ""}
                                onChange={(e) => handleAssetCommentChange(a.localId, e.target.value)}
                                placeholder="e.g., This is my CV, use it to populate education and experience."
                                className={`flex-1 px-3 py-2 border rounded-md shadow-sm bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.glass}`}
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => persistLocalComment(a.localId)}
                                disabled={a.isSavingComment}
                                aria-label="Save"
                                title="Save"
                                className={`inline-flex items-center gap-2 px-3 h-9 rounded-md ${typography.button} ${
                                  a.isSavingComment ? "opacity-60 cursor-not-allowed" : ""
                                } bg-gray-700 dark:bg-dark-600 hover:bg-gray-800 dark:hover:bg-dark-500 text-white transition-all shadow-lg`}
                              >
                                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.29a1 1 0 010 1.414l-7.2 7.2a1 1 0 01-1.414 0l-3.6-3.6a1 1 0 011.414-1.414l2.893 2.893 6.493-6.493a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Save</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => cancelEditLocalComment(a.localId)}
                                disabled={a.isSavingComment}
                                className={`px-2 py-2 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.button} ${textColors.secondary} ${
                                  a.isSavingComment ? "opacity-60 cursor-not-allowed" : ""
                                }`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : null}

                        {a.commentSaveError ? (
                          <div className={`mt-1 ${typography.caption} text-red-600 dark:text-red-400`}>{a.commentSaveError}</div>
                        ) : null}
                      </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
                </div>
            </div>

            {/* Step 5: Global AI Prompt (optional) */}
            <div
              ref={promptCardRef}
              className={`${surfaces.glassCard} rounded-3xl border ${borders.glass} p-6 shadow-sm ${promptCardLayout}`}
              onMouseEnter={() => isStreamline && setHoverHintArea("prompt")}
              onMouseLeave={() => isStreamline && setHoverHintArea(null)}
            >
              <div className="h-full flex flex-col">
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div
                    className={`relative h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden ${stepGlowClass(
                      "prompt"
                    )}`}
                  >
                    <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500" />
                    <span
                      aria-hidden="true"
                      className={`absolute inset-0 bg-green-400 transition-opacity duration-1000 ease-out ${
                        isReadyToCreate ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="relative">{isStreamline ? 3 : 5}</span>
                  </div>
                  <span
                    className={`sm:hidden px-2 py-0.5 rounded-full border ${borders.default} ${surfaces.secondary} ${typography.caption} ${textColors.secondary} text-[10px] whitespace-nowrap`}
                  >
                    Optional
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className={`${typography.h4} ${textColors.primary}`}>
                      {isStreamline
                        ? "Outline website. What should be where?"
                        : "Global AI prompt"}
                    </h2>
                      <span
                        className={`hidden sm:inline-flex px-2 py-1 rounded-full border ${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${typography.caption} ${textColors.secondary} shrink-0`}
                      >
                      Optional
                    </span>
                  </div>
                  <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                    {isStreamline
                      ? "Be detailed — the more you write, the better our AI can build."
                      : "Add high-level instructions (tone, audience, what to emphasize, what to avoid)."}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex-1 flex flex-col min-h-0 space-y-6">
                <div className="space-y-0 divide-y divide-gray-200/70 dark:divide-dark-700/70">
                  <div className="pb-4">
                    <div className={`${typography.label} ${textColors.primary} mb-2`}>
                      Primary goal of the website
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {OUTLINE_HELP_PRIMARY_GOAL_OPTIONS.map((opt) => {
                        const selected = outlineHelpPrimaryGoal === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleOutlineHelpPrimaryGoalSelect(opt.value)}
                            className={`px-2.5 py-1.5 rounded-md border ${typography.caption} font-medium transition-colors whitespace-nowrap ${
                              selected
                                ? "border-green-400 bg-green-400 text-white dark:text-white"
                                : `${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} ${textColors.primary}`
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {outlineHelpPrimaryGoal === "other" ? (
                      <div className="mt-2">
                        <input
                          type="text"
                          value={outlineHelpPrimaryGoalOtherText}
                          onChange={(e) => setOutlineHelpPrimaryGoalOtherText(e.target.value)}
                          placeholder="Write your primary goal..."
                          className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.glass}`}
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="py-4">
                    <div className={`${typography.label} ${textColors.primary} mb-2`}>Tone</div>
                    {/* Mobile: compact select (prevents horizontal scroll); Desktop: button row */}
                    <div className="sm:hidden">
                      <select
                        value={outlineHelpTone || ""}
                        onChange={(e) => setOutlineHelpTone(e.target.value || null)}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.glass}`}
                        aria-label="Tone"
                      >
                        <option value="">No preference</option>
                        {OUTLINE_HELP_TONE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="hidden sm:flex flex-wrap items-center justify-center gap-2">
                      {OUTLINE_HELP_TONE_OPTIONS.map((opt) => {
                        const selected = outlineHelpTone === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleOutlineHelpToneSelect(opt.value)}
                            className={`px-2.5 py-1.5 rounded-md border ${typography.caption} font-medium transition-colors whitespace-nowrap ${
                              selected
                                ? "border-green-400 bg-green-400 text-white dark:text-white"
                                : `${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} ${textColors.primary}`
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="py-4">
                    <div className={`${typography.label} ${textColors.primary} mb-2`}>
                      How do you want the information outlined?
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      step={1}
                      value={outlineHelpFormat}
                      onChange={handleOutlineHelpFormatChange}
                      className="w-full"
                      aria-label="Outline format preference"
                    />
                    <div className={`mt-2 grid grid-cols-5 gap-2 ${typography.caption} ${textColors.tertiary}`}>
                      <div className="text-left">All bullets</div>
                      <div className="text-center">More bullets</div>
                      <div className="text-center">Mixed</div>
                      <div className="text-center">More text</div>
                      <div className="text-right">All text</div>
                    </div>
                  </div>

                  <div className="py-4">
                    <div className={`${typography.label} ${textColors.primary} mb-2`}>
                      How long should the texts be?
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      step={1}
                      value={outlineHelpTextLength}
                      onChange={handleOutlineHelpTextLengthChange}
                      className="w-full"
                      aria-label="Text length preference"
                    />
                    <div className={`mt-2 grid grid-cols-5 gap-2 ${typography.caption} ${textColors.tertiary}`}>
                      <div className="text-left">Very succinct</div>
                      <div className="text-center">Succinct</div>
                      <div className="text-center">Neutral</div>
                      <div className="text-center">Detailed</div>
                      <div className="text-right">Very detailed</div>
                    </div>
                  </div>

                  <div className="py-4">
                    <div className={`${typography.label} ${textColors.primary} mb-2`}>
                      Which sections would you like? Leave blank for maximum AI creativity
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:max-w-2xl sm:mx-auto">
                      {OUTLINE_HELP_SECTION_OPTIONS.map((opt) => {
                        const selected = outlineHelpSections.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleOutlineHelpSection(opt.value)}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border ${typography.caption} font-medium transition-colors ${
                              selected
                                ? "border-green-400 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                                : `${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} ${textColors.primary}`
                            }`}
                          >
                            <span className="inline-flex items-center justify-center w-4">
                              {selected ? (
                                <svg
                                  className="w-4 h-4 text-green-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.29a1 1 0 010 1.414l-7.2 7.2a1 1 0 01-1.414 0l-3.6-3.6a1 1 0 011.414-1.414l2.893 2.893 6.493-6.493a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <span className="w-3.5 h-3.5 rounded border border-gray-400 dark:border-dark-400" />
                              )}
                            </span>
                            <span>{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className={`${typography.label} ${textColors.primary}`}>
                        Narrate in
                      </div>
                      <div className="flex items-center gap-2">
                        {OUTLINE_HELP_NARRATION_OPTIONS.map((opt) => {
                          const selected = outlineHelpNarrateAsThirdPerson === opt.thirdPerson;
                          return (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() => handleOutlineHelpNarrationSelect(opt.thirdPerson)}
                              aria-pressed={selected}
                              className={`px-2.5 py-1.5 rounded-md border ${typography.caption} font-medium transition-colors whitespace-nowrap ${
                                selected
                                  ? "border-blue-500 bg-blue-500 text-white dark:text-white"
                                  : `${borders.glass} bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${surfaces.hover} ${textColors.primary}`
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200/70 dark:border-dark-700/70 pt-4">
                  <div className={`${typography.label} ${textColors.primary} mb-2`}>
                    Additional Instructions/Information
                  </div>
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onFocus={() => setIsAiPromptFocused(true)}
                    onBlur={() => setIsAiPromptFocused(false)}
                    placeholder={
                      isStreamline
                        ? (isAiPromptFocused ? streamlinePromptPlaceholderFull : streamlinePromptPlaceholderShort)
                        : nonStreamlinePromptPlaceholder
                    }
                    className={`w-full flex-1 px-3 py-2 border rounded-md shadow-sm resize-none bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.glass} text-sm leading-snug transition-all duration-300 ease-in-out ${isAiPromptFocused || (aiPrompt.length > 0) ? 'min-h-[160px]' : 'min-h-[60px]'}`}
                  />
                  <div className={`${typography.caption} ${textColors.secondary} mt-2`}>
                    This will be stored with your website and used by AI features.
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CreateNewWebsite; 